import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API from '../config';
import { Helmet } from 'react-helmet-async';

const T = {
  bg:'#0f1011', surface:'#161718', surfaceHi:'#1e2022', border:'#2a2b2f', borderHi:'#3a3b3f',
  text:'#e8e9ea', textMuted:'#6b6e75', textDim:'#3e4046',
  yes:'#16c784', yesDim:'rgba(22,199,132,0.1)', no:'#ea3943', noDim:'rgba(234,57,67,0.1)',
  accent:'#3772ff', accentDim:'rgba(55,114,255,0.1)', gold:'#f3a94a', goldDim:'rgba(243,169,74,0.1)',
  purple:'#9b5de5',
};

const input = {
  width:'100%', background:T.surface, border:`1px solid ${T.border}`,
  borderRadius:7, padding:'10px 12px', color:T.text, fontSize:13, outline:'none',
  boxSizing:'border-box', fontFamily:"'DM Sans', sans-serif", transition:'border-color 0.15s',
};

const label = {
  color:T.textMuted, fontSize:11, display:'block', marginBottom:6,
  fontWeight:600, textTransform:'uppercase', letterSpacing:'0.07em',
};

const SETTING_META = {
  min_bet:              { label:'Min Trade (KES)',       desc:'Minimum trade amount per prediction' },
  max_bet:              { label:'Max Trade (KES)',       desc:'Maximum trade amount per prediction' },
  max_bets_per_minute:  { label:'Rate Limit (bets/min)', desc:'Max bets per user per minute' },
  max_balance:          { label:'Max Balance (KES)',     desc:'Maximum user wallet balance' },
  duplicate_bet_window: { label:'Duplicate Window (sec)', desc:'Block repeat bets on same market' },
  platform_fee:         { label:'Platform Fee (%)',      desc:'Fee taken from winning pool (default 2%)' },
};

const CATEGORIES = ['politics','football','economics','entertainment','business','events','sports','technology','health','news','global'];

const KENYA_COUNTIES = [
  'Baringo','Bomet','Bungoma','Busia','Elgeyo-Marakwet','Embu','Garissa',
  'Homa Bay','Isiolo','Kajiado','Kakamega','Kericho','Kiambu','Kilifi',
  'Kirinyaga','Kisii','Kisumu','Kitui','Kwale','Laikipia','Lamu','Machakos',
  'Makueni','Mandera','Marsabit','Meru','Migori','Mombasa','Murang\'a',
  'Nairobi','Nakuru','Nandi','Narok','Nyamira','Nyandarua','Nyeri',
  'Samburu','Siaya','Taita-Taveta','Tana River','Tharaka-Nithi','Trans Nzoia',
  'Turkana','Uasin Gishu','Vihiga','Wajir','West Pokot',
];
const toUTC = (localStr) => {
  if (!localStr) return '';
  return new Date(localStr).toISOString();
};
// ✅ FIXED: Authenticated CSV download helper.
// Previously used window.open() which doesn't send Authorization headers → 401.
// Now uses axios with responseType:'blob' and programmatic anchor download.
const downloadCSV = async (url, filename, token) => {
  try {
    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    const href = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = href; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(href);
  } catch (err) {
    console.error('[downloadCSV]', err.message);
    alert('Export failed. Please try again.');
  }
};

const ViolationsTab = ({ token, T }) => {
  const [violations, setViolations] = useState([]);
  const [filter, setFilter] = useState('open');

  useEffect(() => {
    axios.get(`${API}/api/violations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setViolations(r.data)).catch(console.error);
  }, [token]);

  const review = async (id, status) => {
    await axios.put(`${API}/api/violations/${id}/review`, { status }, { headers: { Authorization: `Bearer ${token}` } });
    setViolations(prev => prev.map(v => v.id === id ? { ...v, status } : v));
  };

  const severityColor = s => s === 'high' ? T.no : s === 'medium' ? T.gold : T.textMuted;
  const filtered = violations.filter(v => filter === 'all' || v.status === filter);

  return (
    <div>
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {['open','reviewed','dismissed','all'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ background:filter===f?T.accentDim:T.surface, color:filter===f?T.accent:T.textMuted, border:`1px solid ${filter===f?T.accent+'44':T.border}`, borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:12, fontWeight:600, textTransform:'capitalize' }}>
            {f} {f!=='all'&&`(${violations.filter(v=>v.status===f).length})`}
          </button>
        ))}
      </div>
      {filtered.length === 0
        ? <p style={{ color:T.textMuted, fontSize:13, padding:'32px 0', textAlign:'center' }}>No violations found.</p>
        : filtered.map(v => (
          <div key={v.id} style={{ background:T.surface, border:`1px solid ${v.status==='open'?severityColor(v.severity)+'33':T.border}`, borderRadius:10, padding:'16px 18px', marginBottom:10 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, marginBottom:8 }}>
              <div>
                <span style={{ background:severityColor(v.severity)+'22', color:severityColor(v.severity), fontSize:10, fontWeight:800, padding:'3px 10px', borderRadius:100, textTransform:'uppercase', letterSpacing:'0.08em', marginRight:8 }}>{v.severity}</span>
                <span style={{ color:T.text, fontSize:13, fontWeight:700 }}>{v.type.replace(/_/g,' ').toUpperCase()}</span>
              </div>
              <span style={{ color:T.textDim, fontSize:11, fontFamily:'monospace', flexShrink:0 }}>{new Date(v.created_at).toLocaleString('en-KE')}</span>
            </div>
            <p style={{ color:T.textMuted, fontSize:13, marginBottom:6 }}>{v.description}</p>
            <p style={{ color:T.textDim, fontSize:12, marginBottom:v.status==='open'?12:0 }}>
              User: <span style={{ color:T.accent }}>{v.full_name}</span> · {v.phone} · {v.is_suspended?<span style={{ color:T.no }}>Suspended</span>:<span style={{ color:T.yes }}>Active</span>}
            </p>
            {v.status === 'open' && (
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => review(v.id,'reviewed')} style={{ background:T.accentDim, color:T.accent, border:`1px solid ${T.accent}33`, borderRadius:7, padding:'6px 14px', cursor:'pointer', fontSize:12, fontWeight:700 }}>Mark Reviewed</button>
                <button onClick={() => review(v.id,'dismissed')} style={{ background:T.surface, color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:7, padding:'6px 14px', cursor:'pointer', fontSize:12, fontWeight:600 }}>Dismiss</button>
              </div>
            )}
          </div>
        ))
      }
    </div>
  );
};

export default function Admin() {
  const [markets, setMarkets]                   = useState([]);
  const [marketRequests, setMarketRequests]     = useState([]);
  const [settings, setSettings]                 = useState({});
  const [editingSettings, setEditingSettings]   = useState({});
  const [settingsSaved, setSettingsSaved]       = useState(false);
  const [resolving, setResolving]               = useState(null);
  const [deleting, setDeleting]                 = useState(null);
  const [voiding, setVoiding]                   = useState(null);
  const [voidReason, setVoidReason]             = useState('');
  const [editingMarket, setEditingMarket]       = useState(null);
  const [editForm, setEditForm]                 = useState({});
  const [editPreview, setEditPreview]           = useState('');
const [newMarket, setNewMarket] = useState({ title:'', description:'', category:'', closes_at:'', image_url:'', rules:'', yes_label:'', no_label:'', location:'national' });
  const [marketType, setMarketType]             = useState('binary');
  const [options, setOptions]                   = useState([{label:'',image_url:''},{label:'',image_url:''}]);
  const [imagePreview, setImagePreview]         = useState('');
  const [tab, setTab]                           = useState('create');
  const [users, setUsers]                       = useState([]);
  const [userSearch, setUserSearch]             = useState('');
  const [suspending, setSuspending]             = useState(null);
  const [suspendReason, setSuspendReason]       = useState('');
  const [adjusting, setAdjusting]               = useState(null);
  const [adjustAmount, setAdjustAmount]         = useState('');
  const [adjustReason, setAdjustReason]         = useState('');
  const [pendingDeposits, setPendingDeposits]   = useState([]);
  const [selectedPending, setSelectedPending]   = useState(null);
  const [viewingTxns, setViewingTxns]           = useState(null);
  const [userTxns, setUserTxns]                 = useState([]);
  const [financials, setFinancials]             = useState(null);
  const [message, setMessage]                   = useState('');
  const [error, setError]                       = useState('');
  const [isAdmin, setIsAdmin]                   = useState(null);
  const [broadcastMsg, setBroadcastMsg]         = useState('');
  const [broadcastTitle, setBroadcastTitle]     = useState('');
  const [broadcastType, setBroadcastType]       = useState('info');
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [marketPerf, setMarketPerf]             = useState([]);
  const [chamas, setChamas]                     = useState([]);
const [chamasLoading, setChamasLoading]       = useState(false);
const [expandedChama, setExpandedChama]       = useState(null);
const [chamaMembers, setChamaMembers]         = useState({});
const [chamaTxns, setChamaTxns]               = useState({});
const [chamaAdjAmount, setChamaAdjAmount]     = useState('');
const [chamaAdjReason, setChamaAdjReason]     = useState('');
const [chamaAdjType, setChamaAdjType]         = useState('credit');
const [messagingChama, setMessagingChama]     = useState(null);
const [chamaMessageText, setChamaMessageText] = useState('');
const [chamaDisputes, setChamaDisputes]       = useState({});
const [adjustingChama, setAdjustingChama]     = useState(null);
const [kraYear, setKraYear]                   = useState(new Date().getFullYear());
  const [kraMonth, setKraMonth]                 = useState(new Date().getMonth()+1);
  const [exportingCSV, setExportingCSV]         = useState('');
  const [messaging, setMessaging] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [allOpenDisputes, setAllOpenDisputes] = useState([]);
const [disputesLoading, setDisputesLoading] = useState(false);
const [disputeActions, setDisputeActions] = useState({});
const [kraRemitAmount, setKraRemitAmount] = useState('');
const [kraRemitRef, setKraRemitRef]       = useState('');
  const { token, user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [adjustType, setAdjustType]         = useState('');
  const [creditType, setCreditType]         = useState('');
  const [debitType, setDebitType]           = useState('');
const [transferUserId, setTransferUserId]       = useState('');
  const [transferUserSearch, setTransferUserSearch] = useState('');
  
  const [seedingMarket, setSeedingMarket]   = useState(null); 
const [seedAmounts, setSeedAmounts]       = useState({});   
const [seedSource, setSeedSource]         = useState('platform_wallet');
const [seedLoading, setSeedLoading]       = useState(false);
const [seedBalances, setSeedBalances] = useState(null);

const reversibleDebitBalance =
    (financials?.fee_breakdown?.admin_debits_recovery || 0) +
    (financials?.fee_breakdown?.admin_debits_transfer || 0) +
    (financials?.fee_breakdown?.seed_debit_unlocked || 0) -
    (financials?.fee_breakdown?.admin_credits_reversal || 0) -
    (financials?.fee_breakdown?.seed_debit_locked || 0);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    checkAdmin();
  }, [token]);

  useEffect(() => {
    const t = setTimeout(() => setImagePreview(newMarket.image_url), 600);
    return () => clearTimeout(t);
  }, [newMarket.image_url]);

  useEffect(() => {
    const t = setTimeout(() => setEditPreview(editForm.image_url), 600);
    return () => clearTimeout(t);
  }, [editForm.image_url]);

  const checkAdmin = async () => {
  try {
    const r = await axios.get(`${API}/api/users/profile`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.data.is_admin) { 
      updateUser({ is_super_admin: r.data.is_super_admin });
      setIsAdmin(true); 
      fetchAll(); 
    }
    else setIsAdmin(false);
  } catch { setIsAdmin(false); }
};

  const fetchAll = () => { fetchMarkets(); fetchSettings(); fetchRequests(); fetchUsers(); fetchFinancials(); fetchMarketPerf(); fetchChamas(); fetchAllOpenDisputes(); };

  const fetchMarkets = async () => {
    try { const r = await axios.get(`${API}/api/markets`); setMarkets(r.data); } catch (e) { console.error(e); }
  };

  const fetchSettings = async () => {
    try {
      const r = await axios.get(`${API}/api/settings`, { headers: { Authorization: `Bearer ${token}` } });
      const map = {}; r.data.forEach(s => { map[s.key] = s.value; });
      setSettings(map); setEditingSettings(map);
    } catch (e) { console.error(e); }
  };

   const fetchFinancials = async () => {
    try {
      const r = await axios.get(`${API}/api/users/admin/financials`, { headers: { Authorization: `Bearer ${token}` } });
      setFinancials(r.data);
    } catch (e) { console.error(e); }
  };

  const fetchMarketPerf = async () => {
  try {
    const r = await axios.get(`${API}/api/markets/admin/performance`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setMarketPerf(r.data);
  } catch (e) { console.error(e); }
};

const fetchChamas = async () => {
  setChamasLoading(true);
  try {
    const r = await axios.get(`${API}/api/chamas/admin/all`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setChamas(r.data.chamas || []);
  } catch (e) { console.error(e); }
  finally { setChamasLoading(false); }
};

const fetchChamaMembers = async (id) => {
  if (chamaMembers[id]) return;
  try {
    const r = await axios.get(`${API}/api/chamas/admin/${id}/members`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setChamaMembers(prev => ({ ...prev, [id]: r.data.members || [] }));
  } catch (e) { console.error(e); }
};

const fetchChamaTxns = async (id) => {
  if (chamaTxns[id]) return;
  try {
    const r = await axios.get(`${API}/api/chamas/admin/${id}/transactions`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setChamaTxns(prev => ({ ...prev, [id]: r.data.transactions || [] }));
  } catch (e) { console.error(e); }
};

const handleChamaAdjust = async (chamaId) => {
  if (!chamaAdjAmount || !chamaAdjReason) return notify('Amount and reason required', true);
  const signed = chamaAdjType === 'debit'
    ? -Math.abs(parseFloat(chamaAdjAmount))
    :  Math.abs(parseFloat(chamaAdjAmount));
  try {
    const r = await axios.post(`${API}/api/chamas/admin/${chamaId}/adjust`, {
      amount: signed,
      reason: chamaAdjReason,
    }, { headers: { Authorization: `Bearer ${token}` } });
    notify(r.data.message);
    setAdjustingChama(null);
    setChamaAdjAmount(''); setChamaAdjReason(''); setChamaAdjType('credit');
    fetchChamas();
    fetchFinancials();
  } catch (err) { notify(err.response?.data?.error || 'Failed to adjust', true); }
};



const handleMessageChamaAdmin = async (chama) => {
  if (!chamaMessageText.trim()) return;
  const adminUser = users.find(u => u.username === chama.admin_username);
  if (!adminUser) return notify('Could not find admin user', true);
  try {
    await axios.post(
      `${API}/api/users/admin/users/${adminUser.id}/message`,
      { message: chamaMessageText.trim() },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    notify(`Message sent to @${chama.admin_username}`);
    setMessagingChama(null);
    setChamaMessageText('');
  } catch (err) { notify(err.response?.data?.message || 'Failed to send', true); }
};

const fetchChamaDisputes = async (id) => {
  if (chamaDisputes[id]) return;
  try {
    const r = await axios.get(`${API}/api/chamas/${id}/disputes`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setChamaDisputes(prev => ({ ...prev, [id]: r.data.disputes || [] }));
  } catch (e) { console.error(e); }
};
const fetchAllOpenDisputes = async () => {
  setDisputesLoading(true);
  try {
    const r = await axios.get(`${API}/api/chamas/admin/disputes/open`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setAllOpenDisputes(r.data.disputes || []);
  } catch (e) { console.error(e); }
  finally { setDisputesLoading(false); }
};

const updateDA = (id, updates) =>
  setDisputeActions(prev => ({ ...prev, [id]: { ...prev[id], ...updates } }));

const handleDisputeUnderReview = async (chamaId, disputeId) => {
  try {
    const r = await axios.patch(
      `${API}/api/chamas/admin/${chamaId}/disputes/${disputeId}/review`,
      { freeze_chama: true },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    notify(r.data.message);
    setAllOpenDisputes(prev =>
      prev.map(d => d.id === disputeId ? { ...d, status: 'under_review', is_frozen: true } : d)
    );
    setDisputeActions(prev => { const n = { ...prev }; delete n[disputeId]; return n; });
  } catch (err) { notify(err.response?.data?.error || 'Failed', true); }
};

const handleResolveChamaDispute = async (chamaId, disputeId, action, note, successorId = null) => {
  const statusMap = { upheld: 'resolved', warning: 'warning', dismissed: 'dismissed' };
  const status = statusMap[action];
  if (!status) return;
  try {
    const r = await axios.patch(
      `${API}/api/chamas/admin/${chamaId}/disputes/${disputeId}/resolve`,
      {
        status,
        resolution: note,
        ...(successorId ? { transfer_admin_to: successorId } : {}),
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    notify(r.data.message + (r.data.unfrozen ? ' · Chama unfrozen.' : '') + (r.data.admin_transferred ? ' · Admin role transferred.' : ''));
    // Update chama-level disputes cache
    setChamaDisputes(prev => ({
      ...prev,
      [chamaId]: (prev[chamaId] || []).map(d =>
        d.id === disputeId ? { ...d, status, resolution: note } : d
      ),
    }));
    setAllOpenDisputes(prev => prev.filter(d => d.id !== disputeId));
    setDisputeActions(prev => { const n = { ...prev }; delete n[disputeId]; return n; });
    if (successorId) fetchChamas(); // refresh chama list to show new admin
  } catch (err) { notify(err.response?.data?.error || 'Failed to resolve', true); }
};

  const fetchUsers = async () => {
    try {
      const r = await axios.get(`${API}/api/users/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(r.data);
    } catch (e) { console.error(e); }
  };

  const fetchUserTxns = async (userId) => {
    try {
      const r = await axios.get(`${API}/api/users/admin/users/${userId}/transactions`, { headers: { Authorization: `Bearer ${token}` } });
      setUserTxns(r.data);
    } catch (e) { console.error(e); }
  };

  const handleSuspend = async (userId) => {
    try {
      await axios.put(`${API}/api/users/admin/users/${userId}/suspend`, { reason:suspendReason }, { headers: { Authorization: `Bearer ${token}` } });
      notify('User suspended'); setSuspending(null); setSuspendReason(''); fetchUsers();
    } catch (err) { notify(err.response?.data?.message||'Failed to suspend', true); }
  };

  const handleUnsuspend = async (userId) => {
    try {
      await axios.put(`${API}/api/users/admin/users/${userId}/unsuspend`, {}, { headers: { Authorization: `Bearer ${token}` } });
      notify('User unsuspended'); fetchUsers();
    } catch (err) { notify(err.response?.data?.message||'Failed to unsuspend', true); }
  };

  const fetchPendingDeposits = async (userId) => {
    try {
      const r = await axios.get(`${API}/api/users/admin/users/${userId}/pending-deposits`, { headers: { Authorization: `Bearer ${token}` } });
      setPendingDeposits(r.data);
    } catch (_) { setPendingDeposits([]); }
  };
  const handleSendMessage = async (userId) => {
  if (!messageText.trim()) return;
  try {
    await axios.post(
      `${API}/api/users/admin/users/${userId}/message`,
      { message: messageText.trim() },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    notify('Message sent');
    setMessaging(null);
    setMessageText('');
  } catch (err) {
    notify(err.response?.data?.message || 'Failed to send message', true);
  }
};

const handleAdjustBalance = async (userId) => {
  try {
    const payload = {
      amount: adjustType === 'debit' ? -Math.abs(parseFloat(adjustAmount)) : Math.abs(parseFloat(adjustAmount)),
      reason: adjustReason,
      ...(selectedPending ? { pending_transaction_id: selectedPending.id } : {}),
      ...(adjustType === 'credit' ? { credit_type: creditType } : {}),
      ...(adjustType === 'debit'  ? { debit_type: debitType }   : {}),
      ...((creditType === 'transfer' || debitType === 'transfer') ? { transfer_user_id: transferUserId } : {}),
    };
      await axios.put(`${API}/api/users/admin/users/${userId}/adjust-balance`, payload, { headers: { Authorization: `Bearer ${token}` } });
      notify(selectedPending ? 'Balance credited and pending deposit cleared' : 'Balance adjusted');
      setAdjusting(null); setAdjustAmount(''); setAdjustReason(''); setSelectedPending(null); setPendingDeposits([]);
      fetchUsers();
      fetchFinancials();
    } catch (err) { notify(err.response?.data?.message || 'Failed to adjust', true); }
  };

  const fetchRequests = async () => {
    try {
      const r = await axios.get(`${API}/api/market-requests/all`, { headers: { Authorization: `Bearer ${token}` } });
      setMarketRequests(r.data);
    } catch (e) { console.error(e); }
  };

  const notify = (msg, isErr=false) => {
    if (isErr) { setError(msg); setMessage(''); } else { setMessage(msg); setError(''); }
    setTimeout(() => { setMessage(''); setError(''); }, 4000);
  };

  const handleSaveSettings = async () => {
    try {
      await Promise.all(Object.entries(editingSettings).map(([key,value]) =>
        axios.put(`${API}/api/settings/${key}`, { value }, { headers: { Authorization: `Bearer ${token}` } })
      ));
      setSettings(editingSettings); setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch { notify('Failed to save settings', true); }
  };

  const handleResolve = async (marketId, outcome, winningOptionId) => {
    try {
      const r = await axios.put(`${API}/api/markets/${marketId}/resolve`,
        { outcome, winning_option_id:winningOptionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notify(`${r.data.message} · Pool: KES ${parseFloat(r.data.total_pool).toLocaleString()} · Fee: KES ${parseFloat(r.data.platform_fee).toLocaleString()} · Paid: KES ${parseFloat(r.data.total_paid||0).toLocaleString()}`);
      setResolving(null); fetchMarkets(); fetchFinancials();
    } catch (err) { notify(err.response?.data?.message||'Failed to resolve', true); }
  };

  const handleCreateMarket = async (e) => {
    e.preventDefault();
    try {
      const filteredOptions = marketType==='multiple'
        ? options.filter(o=>o.label.trim()).map(o=>({ label:o.label.trim(), image_url:o.image_url.trim()||null }))
        : [];
      await axios.post(`${API}/api/markets`, { ...newMarket, closes_at: toUTC(newMarket.closes_at), market_type:marketType, options:filteredOptions }, { headers: { Authorization: `Bearer ${token}` } });
      notify('Market created successfully');
       setNewMarket({ title:'', description:'', category:'', closes_at:'', image_url:'', rules:'', yes_label:'', no_label:'', location:'national' });
      setImagePreview(''); setOptions([{label:'',image_url:''},{label:'',image_url:''}]); setMarketType('binary');
      fetchMarkets();
    } catch (err) { notify(err.response?.data?.message||'Failed to create market', true); }
  };

  const handleEditMarket = async (e) => {
    e.preventDefault();
    try {
      const editPayload = { ...editForm, closes_at: toUTC(editForm.closes_at) };
await axios.put(`${API}/api/markets/${editingMarket}/edit`, editPayload, { headers: { Authorization: `Bearer ${token}` } });
      notify('Market updated'); setEditingMarket(null); setEditForm({}); setEditPreview(''); fetchMarkets();
    } catch (err) { notify(err.response?.data?.message||'Failed to update', true); }
  };

  const handleVoid = async (id) => {
    try {
      const r = await axios.put(`${API}/api/markets/${id}/void`, { reason:voidReason }, { headers: { Authorization: `Bearer ${token}` } });
      notify(r.data.message); setVoiding(null); setVoidReason(''); fetchMarkets();
    } catch (err) { notify(err.response?.data?.message||'Failed to void market', true); setVoiding(null); }
  };

  const fetchSeedBalances = async () => {
  try {
    const r = await axios.get(`${API}/api/markets/admin/seed-balances`, { headers: { Authorization: `Bearer ${token}` } });
    setSeedBalances(r.data);
  } catch { setSeedBalances(null); }
};

const seedAvailable = seedBalances
  ? (seedSource === 'platform_wallet' ? seedBalances.wallet : Math.max(0, seedBalances.debit))
  : 0;

const seedTotalFor = (market) => market.market_type === 'multiple'
  ? (market.options || []).reduce((s, o) => s + (parseFloat(seedAmounts[o.id]) || 0), 0)
  : (parseFloat(seedAmounts.yes) || 0) + (parseFloat(seedAmounts.no) || 0);

const handleSeedMarket = async (market) => {
  const t = seedTotalFor(market);
  if (t <= 0) return notify('Enter a seed amount', true);
  if (t > seedAvailable + 0.001)
    return notify(`Exceeds available balance (KES ${seedAvailable.toLocaleString()})`, true);

  setSeedLoading(true);
  try {
    const isMultiple = market.market_type === 'multiple';
    const amounts = isMultiple
      ? { options: (market.options || []).map(o => ({ option_id: o.id, amount: parseFloat(seedAmounts[o.id] || 0) })) }
      : { yes: parseFloat(seedAmounts.yes || 0), no: parseFloat(seedAmounts.no || 0) };

    const r = await axios.post(
      `${API}/api/markets/${market.id}/seed`,
      { amounts, source: seedSource },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    notify(r.data.message);
    setSeedingMarket(null);
    setSeedAmounts({});
    setSeedSource('platform_wallet');
    fetchMarkets();
    fetchFinancials();
  } catch (err) {
    notify(err.response?.data?.message || 'Failed to seed market', true);
  } finally {
    setSeedLoading(false);
  }
};
  
  const confirmDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/markets/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      notify('Market deleted'); setDeleting(null);
      if (editingMarket===id) { setEditingMarket(null); setEditForm({}); }
      fetchMarkets();
    } catch (err) { notify(err.response?.data?.message||'Failed to delete', true); setDeleting(null); }
  };

  const startEdit = (market) => {
    setEditingMarket(market.id);
   setEditForm({ title:market.title||'', description:market.description||'', category:market.category||'', closes_at:market.closes_at?new Date(market.closes_at).toLocaleString('sv-SE', {timeZone:'Africa/Nairobi'}).slice(0,16):'', image_url:market.image_url||'', rules:market.rules||'', yes_label:market.yes_label||'', no_label:market.no_label||'', location:market.location||'national' });

    setEditPreview(market.image_url||'');
  };

  const updateOption = (i, field, val) => { const u=[...options]; u[i]={...u[i],[field]:val}; setOptions(u); };

  // ✅ FIXED: Authenticated CSV export helper using axios blob download
  const handleExportCSV = async (endpoint, filename) => {
    setExportingCSV(filename);
    try { await downloadCSV(`${API}${endpoint}`, filename, token); }
    finally { setExportingCSV(''); }
  };

  const openMarkets = markets.filter(m=>m.status==='open'||m.status==='closed').sort((a,b)=>new Date(a.closes_at)-new Date(b.closes_at));
  const resolvedMarkets = markets.filter(m=>m.status==='resolved');
  const pendingRequests = marketRequests.filter(r=>r.status==='pending');
  const allEditable = [...openMarkets,...resolvedMarkets];
  const statusColor = (s) => s==='open'?T.yes:s==='closed'?T.gold:s==='resolved'?T.textMuted:T.textDim;

  if (isAdmin===null) return (
    <div style={{ minHeight:'100vh', background:T.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <p style={{ color:T.textMuted, fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>Verifying access...</p>
    </div>
  );

  if (isAdmin===false) return (
    <div style={{ minHeight:'100vh', background:T.bg, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ textAlign:'center', padding:40, maxWidth:360 }}>
        <div style={{ width:48, height:48, borderRadius:'50%', background:T.noDim, border:`1px solid ${T.no}33`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px', fontSize:20 }}>✕</div>
        <h2 style={{ color:T.text, fontSize:18, fontWeight:700, marginBottom:8 }}>Access Denied</h2>
        <p style={{ color:T.textMuted, fontSize:13, marginBottom:24 }}>You don't have permission to view this page.</p>
        <button onClick={() => navigate('/')} style={{ background:T.accent, color:'#fff', border:'none', borderRadius:7, padding:'10px 24px', fontWeight:600, cursor:'pointer', fontSize:13 }}>Go to Markets</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'DM Sans','Segoe UI',sans-serif", color:T.text }}>
       <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500;700&display=swap');
        @keyframes fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        *{box-sizing:border-box;margin:0;padding:0;}
        input:focus,select:focus{border-color:${T.accent}!important;outline:none;}
        ::-webkit-scrollbar{width:3px;}::-webkit-scrollbar-thumb{background:${T.border};border-radius:2px;}
        input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;}
      `}</style>

      <nav style={{ background:`${T.bg}f2`, backdropFilter:'blur(16px)', borderBottom:`1px solid ${T.border}`, height:56, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 28px', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontWeight:800, fontSize:17, color:T.text, letterSpacing:'-0.5px' }}>Mino</span>
          <span style={{ background:T.noDim, color:T.no, fontSize:9, padding:'2px 6px', borderRadius:3, fontWeight:700, letterSpacing:'0.1em', border:`1px solid ${T.no}22` }}>ADMIN</span>
        </div>
        <button onClick={() => navigate('/dashboard')} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:7, padding:'6px 14px', cursor:'pointer', fontSize:12, fontWeight:500 }}>Dashboard</button>
      </nav>

      <div style={{ maxWidth:900, margin:'0 auto', padding:'32px 24px 80px', animation:'fadeUp 0.3s ease' }}>

        {(message||error) && (
          <div style={{ background:message?T.yesDim:T.noDim, border:`1px solid ${message?T.yes:T.no}33`, color:message?T.yes:T.no, padding:'11px 16px', borderRadius:8, marginBottom:20, fontSize:13, fontWeight:500 }}>
            {message||error}
          </div>
        )}

        {/* Stats row */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:28 }}>
          {[
            { label:'Total Markets', value:markets.length, color:T.text },
            { label:'Open / Closed', value:openMarkets.length, color:T.gold },
            { label:'Resolved', value:resolvedMarkets.length, color:T.yes },
            { label:'Pending Requests', value:pendingRequests.length, color:T.purple },
          ].map(s => (
            <div key={s.label} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:9, padding:'16px 18px' }}>
              <p style={{ color:T.textMuted, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>{s.label}</p>
              <p style={{ color:s.color, fontSize:26, fontWeight:800, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tab bar */}
        <div style={{ display:'flex', borderBottom:`1px solid ${T.border}`, marginBottom:24, overflowX:'auto', scrollbarWidth:'none' }}>
          {[
  ['create','Create'],['edit',`Edit (${allEditable.length})`],['resolve',`Resolve (${openMarkets.length})`],
  ['requests',`Requests${pendingRequests.length>0?` · ${pendingRequests.length}`:''}`],
  ...(user?.is_super_admin ? [
    ['chamas', `Chamas${allOpenDisputes.length > 0 ? ` · ${allOpenDisputes.length} dispute${allOpenDisputes.length > 1 ? 's' : ''}` : ''}`],
['disputes', `Disputes${allOpenDisputes.length > 0 ? ` · ${allOpenDisputes.length}` : ''}`],
    ['settings','Fraud Controls'],
    ['users',`Users (${users.length})`],
    ['financials','Financials'],
    ['violations','Violations'],
    ['broadcast','Broadcast'],
  ] : []),
  ['performance','Performance'],
].map(
([key,lbl]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding:'10px 18px', background:'transparent', border:'none',
              borderBottom:`2px solid ${tab===key?T.accent:'transparent'}`,
              color:tab===key?T.text:T.textMuted, fontWeight:tab===key?600:400,
              cursor:'pointer', fontSize:13, whiteSpace:'nowrap', transition:'all 0.12s', marginBottom:-1,
              fontFamily:"'DM Sans',sans-serif",
            }}>{lbl}</button>
          ))}
        </div>

        {/* ── CREATE ── */}
        {tab==='create' && (
          <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:28 }}>
            <h2 style={{ fontSize:15, fontWeight:700, marginBottom:24, color:T.text, letterSpacing:'-0.3px' }}>New Prediction Market</h2>
            <form onSubmit={handleCreateMarket}>
              <div style={{ marginBottom:20 }}>
                <p style={label}>Market Type</p>
                <div style={{ display:'flex', gap:8 }}>
                  {[['binary','Yes / No'],['multiple','Multiple Choice']].map(([val,lbl]) => (
                    <button key={val} type="button" onClick={() => setMarketType(val)} style={{ flex:1, padding:10, borderRadius:7, border:`1px solid ${marketType===val?T.accent:T.border}`, background:marketType===val?T.accentDim:'transparent', color:marketType===val?T.accent:T.textMuted, fontWeight:600, cursor:'pointer', fontSize:13, transition:'all 0.12s', fontFamily:"'DM Sans',sans-serif" }}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div><label style={label}>Title</label><input type="text" value={newMarket.title} onChange={e => setNewMarket({...newMarket,title:e.target.value})} placeholder="Will X happen?" style={input}/></div>
                <div>
                  <label style={label}>Category</label>
                  <select value={newMarket.category} onChange={e => setNewMarket({...newMarket,category:e.target.value})} required style={{...input,cursor:'pointer'}}>
                    <option value="">Select category</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                  </select>
                </div>
              </div>
               <div style={{ marginBottom:14 }}>
                <label style={label}>Location</label>
                <select value={newMarket.location} onChange={e => setNewMarket({...newMarket, location:e.target.value})} style={{...input, cursor:'pointer'}}>
                  <option value="national">National — visible to all</option>
                  {KENYA_COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ marginBottom:14 }}><label style={label}>Description</label><input type="text" value={newMarket.description} onChange={e => setNewMarket({...newMarket,description:e.target.value})} placeholder="Brief context for traders" style={input}/></div>
              <div style={{ marginBottom:14 }}>
                <label style={label}>Resolution Rules</label>
                <textarea value={newMarket.rules} onChange={e => setNewMarket({...newMarket,rules:e.target.value})} placeholder="e.g. This market resolves YES if... Source: official announcement." rows={4} style={{ ...input, resize:'vertical', lineHeight:1.6, minHeight:90 }}/>
                <p style={{ color:T.textDim, fontSize:11, marginTop:5 }}>Shown to users under the Rules tab on the market page</p>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={label}>Image URL (optional)</label>
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                  <input type="text" value={newMarket.image_url} onChange={e => setNewMarket({...newMarket,image_url:e.target.value})} placeholder="https://..." style={{...input,flex:1}}/>
                  <div style={{ width:44, height:44, borderRadius:7, overflow:'hidden', background:T.surfaceHi, border:`1px solid ${T.border}`, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {imagePreview?<img src={imagePreview} alt="" onError={e=>e.target.style.display='none'} style={{ width:'100%',height:'100%',objectFit:'cover' }}/>:<span style={{ color:T.textDim, fontSize:11, fontFamily:"'DM Mono',monospace" }}>IMG</span>}
                  </div>
                </div>
              </div>
              {marketType==='binary' && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                  <div><label style={label}>Yes Label <span style={{ color:T.textDim, fontWeight:400 }}>(optional)</span></label><input type="text" value={newMarket.yes_label} onChange={e => setNewMarket({...newMarket,yes_label:e.target.value})} placeholder="Yes" style={input}/></div>
                  <div><label style={label}>No Label <span style={{ color:T.textDim, fontWeight:400 }}>(optional)</span></label><input type="text" value={newMarket.no_label} onChange={e => setNewMarket({...newMarket,no_label:e.target.value})} placeholder="No" style={input}/></div>
                </div>
              )}
              <div style={{ marginBottom:20 }}><label style={label}>Closing Date & Time</label><input type="datetime-local" value={newMarket.closes_at} onChange={e => setNewMarket({...newMarket,closes_at:e.target.value})} required style={input}/></div>
              {marketType==='multiple' && (
                <div style={{ marginBottom:20 }}>
                  <p style={label}>Options</p>
                  {options.map((opt,i) => (
                    <div key={i} style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
                      <div style={{ width:36, height:36, borderRadius:6, overflow:'hidden', background:T.surfaceHi, border:`1px solid ${T.border}`, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {opt.image_url?<img src={opt.image_url} alt="" onError={e=>e.target.style.display='none'} style={{ width:'100%',height:'100%',objectFit:'cover' }}/>:<span style={{ color:T.textDim, fontSize:9, fontFamily:"'DM Mono',monospace" }}>IMG</span>}
                      </div>
                      <input type="text" value={opt.label} onChange={e => updateOption(i,'label',e.target.value)} placeholder={`Option ${i+1}`} style={{...input,flex:1}}/>
                      <input type="text" value={opt.image_url} onChange={e => updateOption(i,'image_url',e.target.value)} placeholder="Image URL" style={{...input,flex:1,color:T.textMuted,fontSize:12}}/>
                      {options.length>2 && <button type="button" onClick={() => setOptions(options.filter((_,j)=>j!==i))} style={{ background:'transparent', color:T.no, border:`1px solid ${T.border}`, borderRadius:6, padding:'8px 10px', cursor:'pointer', fontWeight:700, flexShrink:0, fontSize:13 }}>✕</button>}
                    </div>
                  ))}
                  <button type="button" onClick={() => setOptions([...options,{label:'',image_url:''}])} style={{ background:'transparent', color:T.accent, border:`1px dashed ${T.accent}55`, borderRadius:7, padding:9, cursor:'pointer', fontSize:13, fontWeight:500, width:'100%', marginTop:4, fontFamily:"'DM Sans',sans-serif" }}>+ Add option</button>
                </div>
              )}
              <button type="submit" style={{ background:T.accent, color:'#fff', border:'none', borderRadius:7, padding:'11px 24px', fontWeight:700, cursor:'pointer', fontSize:13 }}>Create Market</button>
            </form>
          </div>
        )}

        {/* ── EDIT ── */}
        {tab==='edit' && (
          <div>
            {allEditable.length===0
              ? <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:60, textAlign:'center', color:T.textDim }}>No markets to edit</div>
              : allEditable.map(market => (
                <div key={market.id} style={{ background:T.surface, border:`1px solid ${editingMarket===market.id?T.accent:deleting===market.id?T.no:T.border}`, borderRadius:10, padding:20, marginBottom:8, transition:'border-color 0.15s' }}>
                  {deleting===market.id ? (
                    <div>
                      <p style={{ fontWeight:600, fontSize:14, color:T.text, marginBottom:6 }}>{market.title}</p>
                      <div style={{ background:T.noDim, border:`1px solid ${T.no}22`, borderRadius:7, padding:'10px 14px', marginBottom:14 }}>
                        <p style={{ color:T.no, fontWeight:600, fontSize:13, marginBottom:3 }}>This cannot be undone</p>
                        <p style={{ color:T.textMuted, fontSize:12 }}>All bets, comments, and price history will be permanently deleted.</p>
                      </div>
                      <div style={{ display:'flex', gap:8 }}>
                        <button onClick={() => confirmDelete(market.id)} style={{ background:T.no, color:'#fff', border:'none', borderRadius:7, padding:'9px 18px', fontWeight:700, cursor:'pointer', fontSize:13 }}>Delete permanently</button>
                        <button onClick={() => setDeleting(null)} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:7, padding:'9px 14px', cursor:'pointer', fontSize:13 }}>Cancel</button>
                      </div>
                    </div>
                  ) : editingMarket===market.id ? (
                    <form onSubmit={handleEditMarket}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
                        <p style={{ fontWeight:600, fontSize:13, color:T.textMuted }}>Editing market</p>
                        <button type="button" onClick={() => { setEditingMarket(null); setEditForm({}); }} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:6, padding:'5px 12px', cursor:'pointer', fontSize:12 }}>Cancel</button>
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
                        <div><label style={label}>Title</label><input type="text" value={editForm.title} onChange={e => setEditForm({...editForm,title:e.target.value})} required style={input}/></div>
                        <div><label style={label}>Category</label><select value={editForm.category} onChange={e => setEditForm({...editForm,category:e.target.value})} style={{...input,cursor:'pointer'}}>{CATEGORIES.map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}</select></div>
                      </div>
                       <div style={{ marginBottom:12 }}>
                        <label style={label}>Location</label>
                        <select value={editForm.location||'national'} onChange={e => setEditForm({...editForm,location:e.target.value})} style={{...input,cursor:'pointer'}}>
                          <option value="national">National — visible to all</option>
                          {KENYA_COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div style={{ marginBottom:12 }}><label style={label}>Description</label><input type="text" value={editForm.description} onChange={e => setEditForm({...editForm,description:e.target.value})} style={input}/></div>
                      <div style={{ marginBottom:12 }}>
                        <label style={label}>Resolution Rules</label>
                        <textarea value={editForm.rules||''} onChange={e => setEditForm({...editForm,rules:e.target.value})} placeholder="e.g. This market resolves YES if..." rows={4} style={{ ...input, resize:'vertical', lineHeight:1.6, minHeight:90 }}/>
                      </div>
                      <div style={{ marginBottom:12 }}>
                        <label style={label}>Image URL</label>
                        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                          <input type="text" value={editForm.image_url} onChange={e => setEditForm({...editForm,image_url:e.target.value})} placeholder="https://..." style={{...input,flex:1}}/>
                          <div style={{ width:40, height:40, borderRadius:6, overflow:'hidden', background:T.surfaceHi, border:`1px solid ${T.border}`, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                            {editPreview?<img src={editPreview} alt="" onError={e=>e.target.style.display='none'} style={{ width:'100%',height:'100%',objectFit:'cover' }}/>:<span style={{ color:T.textDim, fontSize:9, fontFamily:"'DM Mono',monospace" }}>IMG</span>}
                          </div>
                        </div>
                      </div>
                      {(!market.market_type||market.market_type==='binary') && (
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
                          <div><label style={label}>Yes Label</label><input type="text" value={editForm.yes_label||''} onChange={e => setEditForm({...editForm,yes_label:e.target.value})} placeholder="Yes" style={input}/></div>
                          <div><label style={label}>No Label</label><input type="text" value={editForm.no_label||''} onChange={e => setEditForm({...editForm,no_label:e.target.value})} placeholder="No" style={input}/></div>
                        </div>
                      )}
                      <div style={{ marginBottom:18 }}><label style={label}>Closing Date & Time</label><input type="datetime-local" value={editForm.closes_at} onChange={e => setEditForm({...editForm,closes_at:e.target.value})} style={input}/></div>
                      <div style={{ display:'flex', gap:8 }}>
                        <button type="submit" style={{ background:T.accent, color:'#fff', border:'none', borderRadius:7, padding:'10px 20px', fontWeight:700, cursor:'pointer', fontSize:13 }}>Save changes</button>
                        <button type="button" onClick={() => { setEditingMarket(null); setEditForm({}); }} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:7, padding:'10px 14px', cursor:'pointer', fontSize:13 }}>Cancel</button>
                      </div>
                    </form>
                  ) : (
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                      <div style={{ display:'flex', gap:12, alignItems:'center', flex:1, minWidth:0 }}>
                        {market.image_url
                          ?<img src={market.image_url} alt="" style={{ width:40,height:40,borderRadius:7,objectFit:'cover',flexShrink:0 }} onError={e=>e.target.style.display='none'}/>
                          :<div style={{ width:40,height:40,borderRadius:7,background:T.surfaceHi,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center' }}><span style={{ fontSize:10,color:T.textDim,fontFamily:"'DM Mono',monospace",fontWeight:700 }}>{market.category?.slice(0,3).toUpperCase()}</span></div>
                        }
                        <div style={{ minWidth:0 }}>
                          <p style={{ fontWeight:600, fontSize:13, color:T.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:3 }}>{market.title}</p>
                          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                            <span style={{ fontSize:11, color:T.textMuted }}>{market.category}</span>
                            <span style={{ fontSize:10, color:statusColor(market.status), fontWeight:700, fontFamily:"'DM Mono',monospace" }}>{market.status.toUpperCase()}</span>
                          </div>
                        </div>
                      </div>
                      
                    <div style={{ display:'flex', gap:6, flexShrink:0 }}>
  <button onClick={() => startEdit(market)} style={{ background:T.accentDim, color:T.accent, border:`1px solid ${T.accent}22`, borderRadius:6, padding:'7px 14px', fontWeight:600, cursor:'pointer', fontSize:12 }}>Edit</button>
  {market.status === 'open' && (
    <button
      onClick={() => {
        setSeedingMarket(seedingMarket === market.id ? null : market.id);
        setSeedAmounts({});
        setSeedSource('platform_wallet');
                 fetchSeedBalances();
        fetchFinancials();
      }}
      style={{ background:T.yesDim, color:T.yes, border:`1px solid ${T.yes}22`, borderRadius:6, padding:'7px 14px', fontWeight:600, cursor:'pointer', fontSize:12 }}>
      Seed
    </button>
  )}
  {market.status === 'closed' && (
    <button onClick={async () => {
      const newDate = prompt('Enter new closing date/time (e.g. 2026-05-01T21:00):');
      if (!newDate) return;
      try {
        await axios.put(`${API}/api/markets/${market.id}/reopen`, { closes_at: toUTC(newDate) }, { headers: { Authorization: `Bearer ${token}` } });
        notify('Market reopened'); fetchMarkets();
      } catch (err) { notify(err.response?.data?.message || 'Failed to reopen', true); }
    }} style={{ background:T.yesDim, color:T.yes, border:`1px solid ${T.yes}22`, borderRadius:6, padding:'7px 14px', fontWeight:600, cursor:'pointer', fontSize:12 }}>Reopen</button>
  )}
  <button onClick={() => setDeleting(market.id)} style={{ background:'transparent', color:T.no, border:`1px solid ${T.border}`, borderRadius:6, padding:'7px 12px', fontWeight:600, cursor:'pointer', fontSize:12 }} onMouseEnter={e=>e.currentTarget.style.borderColor=T.no} onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>Delete</button>
</div>

{/* Seed Panel */}
{seedingMarket === market.id && (
  <div style={{ flexBasis:'100%', marginTop:14, background:T.bg, border:`1px solid ${T.yes}33`, borderRadius:8, padding:16 }}>
    <p style={{ color:T.yes, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:14 }}>
      Seed Liquidity — funds inject directly into the LMSR pool
    </p>

    {/* Source selector */}
    <p style={{ ...label, marginBottom:8 }}>Funding Source</p>
    <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              {[
        ['platform_wallet', 'Platform Wallet', `KES ${(seedBalances?.wallet ?? 0).toLocaleString(undefined,{maximumFractionDigits:2})}`],
        ['debit_balance',   'Debit Balance',   `KES ${(seedBalances?.debit ?? 0).toLocaleString(undefined,{maximumFractionDigits:2})}`],
      ].map(([val, lbl, bal]) => (
        <div key={val} onClick={() => setSeedSource(val)}
          style={{ flex:1, padding:'10px 12px', borderRadius:7, cursor:'pointer', transition:'all 0.12s',
            border:`1px solid ${seedSource === val ? T.yes : T.border}`,
            background: seedSource === val ? T.yesDim : 'transparent',
          }}>
          <p style={{ color: seedSource === val ? T.yes : T.text, fontSize:13, fontWeight:600, marginBottom:2 }}>{lbl}</p>
          <p style={{ color:T.textMuted, fontSize:11, fontFamily:"'DM Mono',monospace" }}>{bal} available</p>
        </div>
      ))}
    </div>

    {/* Amount inputs */}
    <p style={{ ...label, marginBottom:8 }}>Seed Amounts (KES)</p>
    {market.market_type === 'multiple' ? (
      <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
        {(market.options || []).map(opt => (
          <div key={opt.id} style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, flex:1 }}>
              {opt.image_url && <img src={opt.image_url} alt="" style={{ width:24, height:24, borderRadius:4, objectFit:'cover' }} onError={e => e.target.style.display='none'}/>}
              <span style={{ color:T.text, fontSize:13, fontWeight:500 }}>{opt.label}</span>
            </div>
            <input
              type="number"
              value={seedAmounts[opt.id] || ''}
              onChange={e => setSeedAmounts(prev => ({ ...prev, [opt.id]: e.target.value }))}
              placeholder="0"
              style={{ ...input, width:120, textAlign:'right', fontFamily:"'DM Mono',monospace" }}
            />
          </div>
        ))}
      </div>
    ) : (
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
        <div>
          <label style={label}>{market.yes_label || 'YES'}</label>
          <input
            type="number"
            value={seedAmounts.yes || ''}
            onChange={e => setSeedAmounts(prev => ({ ...prev, yes: e.target.value }))}
            placeholder="0"
            style={{ ...input, fontFamily:"'DM Mono',monospace" }}
          />
        </div>
        <div>
          <label style={label}>{market.no_label || 'NO'}</label>
          <input
            type="number"
            value={seedAmounts.no || ''}
            onChange={e => setSeedAmounts(prev => ({ ...prev, no: e.target.value }))}
            placeholder="0"
            style={{ ...input, fontFamily:"'DM Mono',monospace" }}
          />
        </div>
      </div>
    )}

    {/* Total preview */}
    {(() => {
      const total = market.market_type === 'multiple'
        ? (market.options || []).reduce((s, o) => s + parseFloat(seedAmounts[o.id] || 0), 0)
        : parseFloat(seedAmounts.yes || 0) + parseFloat(seedAmounts.no || 0);
      return total > 0 ? (
        <div style={{ background:T.yesDim, border:`1px solid ${T.yes}33`, borderRadius:7, padding:'8px 12px', marginBottom:14 }}>
          <p style={{ color:T.yes, fontSize:13, fontWeight:600 }}>
            KES {total.toLocaleString()} will be injected from {seedSource === 'platform_wallet' ? 'Platform Wallet' : 'Debit Balance'}
          </p>
          <p style={{ color:T.textMuted, fontSize:11, marginTop:3 }}>
            Winning side seed returns to platform wallet at resolution. Losing side funds user payouts.
          </p>
        </div>
      ) : null;
    })()}

        {seedTotalFor(market) > seedAvailable + 0.001 && (
      <div style={{ background:T.noDim, border:`1px solid ${T.no}33`, borderRadius:7, padding:'8px 12px', marginBottom:14 }}>
        <p style={{ color:T.no, fontSize:12, fontWeight:600 }}>
          Exceeds available balance (KES {seedAvailable.toLocaleString(undefined,{maximumFractionDigits:2})}). Reduce the amounts.
        </p>
      </div>
    )}

    <div style={{ display:'flex', gap:8 }}>
      <button
        onClick={() => handleSeedMarket(market)}
                disabled={seedLoading || !seedBalances || seedTotalFor(market) <= 0 || seedTotalFor(market) > seedAvailable + 0.001}
        style={{ background: seedLoading ? T.surfaceHi : T.yes, color: seedLoading ? T.textDim : '#fff', border:'none', borderRadius:7, padding:'9px 20px', fontWeight:700, cursor: seedLoading ? 'not-allowed' : 'pointer', fontSize:13 }}>
        {seedLoading ? 'Seeding...' : 'Seed Market'}
      </button>
      <button
        onClick={() => { setSeedingMarket(null); setSeedAmounts({}); setSeedSource('platform_wallet'); }}
        style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:7, padding:'9px 14px', cursor:'pointer', fontSize:13 }}>
        Cancel
      </button>
    </div>
  </div>
)}
                    </div>
                  )}
                </div>
              ))
            }
          </div>
        )}

        {/* ── RESOLVE ── */}
        {tab==='resolve' && (
          <div>
            {openMarkets.length===0
              ? <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:60, textAlign:'center', color:T.textDim }}>No markets pending resolution</div>
              : openMarkets.map(market => {
                const total=parseFloat(market.total_yes)+parseFloat(market.total_no);
                return (
                  <div key={market.id} style={{ background:T.surface, border:`1px solid ${resolving===market.id?T.gold:T.border}`, borderRadius:10, padding:20, marginBottom:8, transition:'border-color 0.15s' }}>
                    <div style={{ display:'flex', gap:12, alignItems:'flex-start', marginBottom:resolving===market.id?16:0 }}>
                      {market.image_url&&<img src={market.image_url} alt="" style={{ width:40,height:40,borderRadius:7,objectFit:'cover',flexShrink:0 }} onError={e=>e.target.style.display='none'}/>}
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                          <div style={{ minWidth:0 }}>
                            <p style={{ fontWeight:600, fontSize:14, color:T.text, marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{market.title}</p>
                            <div style={{ display:'flex', gap:12 }}>
                              <span style={{ color:T.textMuted, fontSize:12 }}>{market.category}</span>
                              <span style={{ color:T.textMuted, fontSize:12, fontFamily:"'DM Mono',monospace" }}>KES {total.toLocaleString()} pool</span>
                              <span style={{ color:statusColor(market.status), fontSize:11, fontWeight:700, fontFamily:"'DM Mono',monospace" }}>{market.status.toUpperCase()}</span>
                            </div>
                          </div>
                          {resolving!==market.id && (
                            <button onClick={() => setResolving(market.id)} style={{ background:T.goldDim, color:T.gold, border:`1px solid ${T.gold}33`, borderRadius:7, padding:'8px 16px', fontWeight:600, cursor:'pointer', fontSize:12, flexShrink:0 }}>Resolve</button>
                          )}
                        </div>
                      </div>
                    </div>
                    {resolving===market.id && (
                      <div style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:16 }}>
                        <p style={{ color:T.gold, fontSize:12, fontWeight:600, marginBottom:14 }}>Select the winning outcome — payouts are permanent and cannot be reversed</p>
                        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                          {market.market_type==='multiple'
                            ? market.options?.map(opt => (
                                <button key={opt.id} onClick={() => handleResolve(market.id,opt.label,opt.id)}
                                  style={{ display:'flex', alignItems:'center', gap:8, background:T.yesDim, color:T.yes, border:`1px solid ${T.yes}33`, borderRadius:7, padding:'9px 16px', fontWeight:600, cursor:'pointer', fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>
                                  {opt.image_url&&<img src={opt.image_url} alt="" style={{ width:20,height:20,borderRadius:'50%',objectFit:'cover' }} onError={e=>e.target.style.display='none'}/>}
                                  {opt.label} wins
                                </button>
                              ))
                            : <>
                                <button onClick={() => handleResolve(market.id,'yes')} style={{ background:T.yesDim, color:T.yes, border:`1px solid ${T.yes}33`, borderRadius:7, padding:'9px 20px', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>{market.yes_label||'YES'} wins</button>
                                <button onClick={() => handleResolve(market.id,'no')} style={{ background:T.noDim, color:T.no, border:`1px solid ${T.no}33`, borderRadius:7, padding:'9px 20px', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>{market.no_label||'NO'} wins</button>
                              </>
                          }
                          <button onClick={() => { setResolving(null); setVoiding(market.id); }} style={{ background:T.noDim, color:T.no, border:`1px solid ${T.no}33`, borderRadius:7, padding:'9px 16px', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>Void & Refund</button>
                          <button onClick={() => setResolving(null)} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:7, padding:'9px 14px', cursor:'pointer', fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>Cancel</button>
                        </div>
                      </div>
                    )}
                    {voiding===market.id && (
                      <div style={{ background:T.bg, border:`1px solid ${T.no}44`, borderRadius:8, padding:16, marginTop:12 }}>
                        <p style={{ color:T.no, fontSize:12, fontWeight:600, marginBottom:10 }}>Void market — all bets will be refunded to users</p>
                        <input type="text" value={voidReason} onChange={e => setVoidReason(e.target.value)} placeholder="Reason for voiding (shown to users)" style={{ ...input, marginBottom:12 }}/>
                        <div style={{ display:'flex', gap:8 }}>
                          <button onClick={() => handleVoid(market.id)} style={{ background:T.no, color:'#fff', border:'none', borderRadius:7, padding:'9px 18px', fontWeight:700, cursor:'pointer', fontSize:13 }}>Confirm Void & Refund</button>
                          <button onClick={() => { setVoiding(null); setVoidReason(''); }} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:7, padding:'9px 14px', cursor:'pointer', fontSize:13 }}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            }
            {resolvedMarkets.length>0 && (
              <div style={{ marginTop:28 }}>
                <p style={{ color:T.textDim, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>Resolved</p>
                {resolvedMarkets.map(market => (
                  <div key={market.id} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, padding:'12px 16px', marginBottom:6, display:'flex', justifyContent:'space-between', alignItems:'center', gap:12 }}>
                    <div style={{ display:'flex', gap:10, alignItems:'center', flex:1, minWidth:0 }}>
                      {market.image_url&&<img src={market.image_url} alt="" style={{ width:32,height:32,borderRadius:5,objectFit:'cover',flexShrink:0 }} onError={e=>e.target.style.display='none'}/>}
                      <p style={{ fontWeight:500, fontSize:13, color:T.textMuted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{market.title}</p>
                    </div>
                    <span style={{ background:T.yesDim, color:T.yes, padding:'3px 10px', borderRadius:4, fontSize:11, fontWeight:700, fontFamily:"'DM Mono',monospace", flexShrink:0 }}>{market.outcome?.toUpperCase()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── FRAUD CONTROLS ── */}
        {tab==='settings' && user?.is_super_admin && (
          <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:28 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <h2 style={{ fontSize:15, fontWeight:700, color:T.text }}>Fraud Controls</h2>
              {settingsSaved&&<span style={{ color:T.yes, fontSize:12, fontWeight:600, fontFamily:"'DM Mono',monospace" }}>Saved</span>}
            </div>
            <p style={{ color:T.textMuted, fontSize:13, marginBottom:24 }}>Server-side limits enforced on every bet. Changes apply immediately.</p>
            <div style={{ border:`1px solid ${T.border}`, borderRadius:8, overflow:'hidden', marginBottom:24 }}>
              {Object.entries(SETTING_META).map(([key,meta],i,arr) => (
                <div key={key} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:16, padding:'14px 18px', borderBottom:i<arr.length-1?`1px solid ${T.border}`:'none' }}>
                  <div>
                    <p style={{ fontSize:13, fontWeight:600, color:T.text, marginBottom:3 }}>{meta.label}</p>
                    <p style={{ fontSize:12, color:T.textMuted }}>{meta.desc}</p>
                  </div>
                  <input type="number" value={editingSettings[key]??''} onChange={e => setEditingSettings(p=>({...p,[key]:e.target.value}))} style={{ ...input, width:110, textAlign:'right', flexShrink:0, fontFamily:"'DM Mono',monospace" }}/>
                </div>
              ))}
            </div>
            <div style={{ background:T.bg, border:`1px solid ${editingSettings['maintenance_mode']==='1'?T.no:T.border}`, borderRadius:8, padding:'16px 18px', marginBottom:20 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <p style={{ fontSize:13, fontWeight:700, color:editingSettings['maintenance_mode']==='1'?T.no:T.text, marginBottom:3 }}>{editingSettings['maintenance_mode']==='1'?'🔴 Maintenance Mode ON':'🟢 Maintenance Mode OFF'}</p>
                  <p style={{ fontSize:12, color:T.textMuted }}>When ON, all users see a maintenance page. Admins can still access the site.</p>
                </div>
                <button onClick={() => setEditingSettings(p=>({...p,maintenance_mode:p['maintenance_mode']==='1'?'0':'1'}))} style={{ background:editingSettings['maintenance_mode']==='1'?T.no:T.yes, color:'#fff', border:'none', borderRadius:7, padding:'9px 20px', fontWeight:700, cursor:'pointer', fontSize:13, flexShrink:0 }}>
                  {editingSettings['maintenance_mode']==='1'?'Turn OFF':'Turn ON'}
                </button>
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={handleSaveSettings} style={{ background:T.accent, color:'#fff', border:'none', borderRadius:7, padding:'10px 22px', fontWeight:700, cursor:'pointer', fontSize:13 }}>Save changes</button>
              <button onClick={() => setEditingSettings(settings)} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:7, padding:'10px 16px', cursor:'pointer', fontSize:13 }}>Reset</button>
            </div>
          </div>
        )}

        {/* ── REQUESTS ── */}
        {tab==='requests' && (
          <div>
            {marketRequests.length===0
              ? <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:60, textAlign:'center', color:T.textDim }}><p style={{ fontSize:14, marginBottom:6 }}>No market requests yet</p><p style={{ fontSize:12 }}>Users can suggest markets from their Profile page</p></div>
              : marketRequests.map(req => (
                <div key={req.id} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:20, marginBottom:8 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontWeight:600, fontSize:14, color:T.text, marginBottom:4 }}>{req.title}</p>
                      <p style={{ color:T.textMuted, fontSize:12, marginBottom:req.description?4:0 }}>{req.full_name} · {req.category} · {new Date(req.created_at).toLocaleDateString()}</p>
                      {req.description&&<p style={{ color:T.textMuted, fontSize:13, lineHeight:1.5 }}>{req.description}</p>}
                      {req.admin_note&&<p style={{ color:T.accent, fontSize:12, marginTop:6 }}>Note: {req.admin_note}</p>}
                    </div>
                    <span style={{ fontSize:10, padding:'3px 10px', borderRadius:4, fontWeight:700, flexShrink:0, marginLeft:12, fontFamily:"'DM Mono',monospace", background:req.status==='approved'?T.yesDim:req.status==='rejected'?T.noDim:T.goldDim, color:req.status==='approved'?T.yes:req.status==='rejected'?T.no:T.gold }}>{req.status.toUpperCase()}</span>
                  </div>
                  {req.status==='pending' && (
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={async () => { await axios.put(`${API}/api/market-requests/${req.id}`, { status:'approved', admin_note:"Great idea! We'll create this market soon." }, { headers:{ Authorization:`Bearer ${token}` } }); fetchRequests(); }} style={{ background:T.yesDim, color:T.yes, border:`1px solid ${T.yes}22`, borderRadius:6, padding:'7px 14px', fontWeight:600, cursor:'pointer', fontSize:12 }}>Approve</button>
                      <button onClick={async () => { await axios.put(`${API}/api/market-requests/${req.id}`, { status:'rejected', admin_note:"This market doesn't meet our guidelines at this time." }, { headers:{ Authorization:`Bearer ${token}` } }); fetchRequests(); }} style={{ background:T.noDim, color:T.no, border:`1px solid ${T.no}22`, borderRadius:6, padding:'7px 14px', fontWeight:600, cursor:'pointer', fontSize:12 }}>Reject</button>
                    </div>
                  )}
                </div>
              ))
            }
          </div>
        )}

        {/* ── USERS ── */}
        {tab==='users' && user?.is_super_admin && (
          <div>
            {/* ✅ FIXED: CSV exports now use authenticated axios download, not window.open() */}
            {user?.is_super_admin && (
  <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
    <button
      disabled={!!exportingCSV}
      onClick={() => handleExportCSV('/api/users/admin/export/users','mino-users.csv')}
      style={{ background:T.accentDim, color:T.accent, border:`1px solid ${T.accent}22`, borderRadius:7, padding:'8px 16px', fontWeight:600, cursor:'pointer', fontSize:13, opacity:exportingCSV==='mino-users.csv'?0.6:1 }}>
      {exportingCSV==='mino-users.csv'?'Exporting...':'Export Users CSV'}
    </button>
    <button
      disabled={!!exportingCSV}
      onClick={() => handleExportCSV('/api/users/admin/export/transactions','mino-transactions.csv')}
      style={{ background:T.goldDim, color:T.gold, border:`1px solid ${T.gold}22`, borderRadius:7, padding:'8px 16px', fontWeight:600, cursor:'pointer', fontSize:13, opacity:exportingCSV==='mino-transactions.csv'?0.6:1 }}>
      {exportingCSV==='mino-transactions.csv'?'Exporting...':'Export Transactions CSV'}
    </button>
  </div>
)}
            <input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search by name, phone or email..." style={{ ...input, marginBottom:16 }}/>
            {users.filter(u => {
              const q=userSearch.toLowerCase();
              return !q||u.full_name?.toLowerCase().includes(q)||u.phone?.includes(q)||u.email?.toLowerCase().includes(q);
            }).map(u => (
              <div key={u.id} style={{ background:T.surface, border:`1px solid ${u.is_suspended?T.no:T.border}`, borderRadius:10, padding:18, marginBottom:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                      <p style={{ fontWeight:700, fontSize:14, color:T.text }}>{u.full_name}</p>
                      {u.username&&<span style={{ color:T.textMuted, fontSize:12 }}>@{u.username}</span>}
                      {u.is_admin&&<span style={{ background:T.noDim, color:T.no, fontSize:9, padding:'2px 6px', borderRadius:3, fontWeight:700 }}>ADMIN</span>}
                      {u.is_suspended&&<span style={{ background:T.noDim, color:T.no, fontSize:9, padding:'2px 6px', borderRadius:3, fontWeight:700 }}>SUSPENDED</span>}
                      {!u.is_verified&&<span style={{ background:T.goldDim, color:T.gold, fontSize:9, padding:'2px 6px', borderRadius:3, fontWeight:700 }}>UNVERIFIED</span>}
                    </div>
                    <p style={{ color:T.textMuted, fontSize:12, marginBottom:2 }}>{u.phone} {u.email?`· ${u.email}`:''}</p>
                    <p style={{ color:T.textMuted, fontSize:12 }}>Balance: <span style={{ color:T.yes, fontFamily:"'DM Mono',monospace", fontWeight:700 }}>KES {parseFloat(u.balance||0).toLocaleString()}</span> · Joined {new Date(u.created_at).toLocaleDateString()}</p>
                    {u.is_suspended&&u.suspension_reason&&<p style={{ color:T.no, fontSize:12, marginTop:4 }}>Reason: {u.suspension_reason}</p>}
                  </div>
                  <div style={{ display:'flex', gap:6, flexShrink:0, flexWrap:'wrap', justifyContent:'flex-end' }}>
                    <button onClick={() => { setViewingTxns(u.id); fetchUserTxns(u.id); }} style={{ background:T.accentDim, color:T.accent, border:`1px solid ${T.accent}22`, borderRadius:6, padding:'6px 12px', fontWeight:600, cursor:'pointer', fontSize:12 }}>Transactions</button>
                    <button onClick={() => { setAdjusting(u.id); setAdjustAmount(''); setAdjustReason(''); setSelectedPending(null); fetchPendingDeposits(u.id); }} style={{ background:T.goldDim, color:T.gold, border:`1px solid ${T.gold}22`, borderRadius:6, padding:'6px 12px', fontWeight:600, cursor:'pointer', fontSize:12 }}>Adjust</button>
                    <button onClick={() => { setMessaging(u.id); setMessageText(''); }} style={{ background:T.accentDim, color:T.accent, border:`1px solid ${T.accent}22`, borderRadius:6, padding:'6px 12px', fontWeight:600, cursor:'pointer', fontSize:12 }}>Message</button>
                    {u.is_suspended
                      ?<button onClick={() => handleUnsuspend(u.id)} style={{ background:T.yesDim, color:T.yes, border:`1px solid ${T.yes}22`, borderRadius:6, padding:'6px 12px', fontWeight:600, cursor:'pointer', fontSize:12 }}>Unsuspend</button>
                      :!u.is_admin&&<button onClick={() => { setSuspending(u.id); setSuspendReason(''); }} style={{ background:T.noDim, color:T.no, border:`1px solid ${T.no}22`, borderRadius:6, padding:'6px 12px', fontWeight:600, cursor:'pointer', fontSize:12 }}>Suspend</button>
                    }
                    {user?.is_super_admin && !u.is_super_admin && (
  u.is_admin
    ? <button onClick={async () => {
        await axios.put(`${API}/api/users/admin/users/${u.id}/set-admin`, { is_admin: false }, { headers: { Authorization: `Bearer ${token}` } });
        notify('Admin removed'); fetchUsers();
      }} style={{ background:T.noDim, color:T.no, border:`1px solid ${T.no}22`, borderRadius:6, padding:'6px 12px', fontWeight:600, cursor:'pointer', fontSize:12 }}>Remove Admin</button>
    : <button onClick={async () => {
        await axios.put(`${API}/api/users/admin/users/${u.id}/set-admin`, { is_admin: true }, { headers: { Authorization: `Bearer ${token}` } });
        notify('Admin granted'); fetchUsers();
      }} style={{ background:T.accentDim, color:T.accent, border:`1px solid ${T.accent}22`, borderRadius:6, padding:'6px 12px', fontWeight:600, cursor:'pointer', fontSize:12 }}>Make Admin</button>
)}
                  </div>
                </div>
                {suspending===u.id && (
                  <div style={{ marginTop:12, background:T.bg, border:`1px solid ${T.no}33`, borderRadius:8, padding:14 }}>
                    <input value={suspendReason} onChange={e => setSuspendReason(e.target.value)} placeholder="Reason for suspension (shown to user)" style={{ ...input, marginBottom:10 }}/>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={() => handleSuspend(u.id)} style={{ background:T.no, color:'#fff', border:'none', borderRadius:7, padding:'8px 16px', fontWeight:700, cursor:'pointer', fontSize:13 }}>Confirm Suspend</button>
                      <button onClick={() => setSuspending(null)} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:7, padding:'8px 12px', cursor:'pointer', fontSize:13 }}>Cancel</button>
                    </div>
                  </div>
                )}
              {adjusting===u.id && (
  <div style={{ marginTop:12, background:T.bg, border:`1px solid ${T.gold}33`, borderRadius:8, padding:14 }}>

    {/* Pending deposits */}
    {pendingDeposits.length > 0 && (
      <div style={{ marginBottom:14 }}>
        <p style={{ color:T.gold, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>
          Pending Deposits — select one to link this credit
        </p>
        {pendingDeposits.map(tx => (
          <div key={tx.id}
            onClick={() => {
              if (selectedPending?.id === tx.id) { setSelectedPending(null); setAdjustAmount(''); }
              else { setSelectedPending(tx); setAdjustAmount(parseFloat(tx.amount).toString()); }
            }}
            style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', borderRadius:7, marginBottom:6, cursor:'pointer', border:`1px solid ${selectedPending?.id===tx.id?T.gold:T.border}`, background:selectedPending?.id===tx.id?T.goldDim:T.surface, transition:'all 0.12s' }}>
            <div>
              <p style={{ fontSize:12, fontWeight:600, color:T.text, marginBottom:2 }}>KES {parseFloat(tx.amount).toLocaleString()}</p>
              <p style={{ fontSize:11, color:T.textMuted, fontFamily:"'DM Mono',monospace" }}>{tx.mpesa_checkout_id||tx.reference} · {new Date(tx.created_at).toLocaleString('en-KE')}</p>
            </div>
            <span style={{ fontSize:10, padding:'2px 8px', borderRadius:4, fontWeight:700, background:selectedPending?.id===tx.id?T.goldDim:T.surfaceHi, color:selectedPending?.id===tx.id?T.gold:T.textMuted, fontFamily:"'DM Mono',monospace" }}>
              {selectedPending?.id===tx.id?'SELECTED':'PENDING'}
            </span>
          </div>
        ))}
      </div>
    )}

    {!selectedPending && (
      <>
        {/* Credit or Debit toggle */}
        <div style={{ marginBottom:12 }}>
          <p style={{ color:T.textMuted, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>Action</p>
          <div style={{ display:'flex', gap:8 }}>
            {[['credit','Credit User'],['debit','Debit User']].map(([val,lbl]) => (
              <button key={val} type="button"
                onClick={() => { setAdjustAmount(''); setAdjustType(val); }}
                style={{ flex:1, padding:'8px 0', borderRadius:7, border:`1px solid ${adjustType===val?(val==='credit'?T.yes:T.no):T.border}`, background:adjustType===val?(val==='credit'?T.yesDim:T.noDim):'transparent', color:adjustType===val?(val==='credit'?T.yes:T.no):T.textMuted, fontWeight:600, cursor:'pointer', fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>{lbl}
              </button>
            ))}
          </div>
        </div>

        {/* Credit type selector */}
        {adjustType==='credit' && (
          <div style={{ marginBottom:12 }}>
            <p style={{ color:T.textMuted, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>Credit Source</p>
            <div style={{ display:'flex', gap:8, flexDirection:'column' }}>
              {[
                ['revenue',  'From Platform Revenue',    'Spends platform earned fees'],
 ['reversal', 'From Debit Pool',          'Credit using funds reclaimed from users'],
                ['transfer', 'User-to-User Transfer',    'Debit another user and credit this one'],
                ].filter(([val]) => val !== 'reversal' || !financials || reversibleDebitBalance > 0)
               .map(([val,lbl,desc]) => (
                <div key={val} onClick={() => setCreditType(val)}
                  style={{ padding:'10px 12px', borderRadius:7, border:`1px solid ${creditType===val?T.accent:T.border}`, background:creditType===val?T.accentDim:'transparent', cursor:'pointer', transition:'all 0.12s' }}>
                  <p style={{ color:creditType===val?T.accent:T.text, fontSize:13, fontWeight:600, marginBottom:2 }}>{lbl}</p>
                  <p style={{ color:T.textMuted, fontSize:11 }}>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Debit type selector */}
        {adjustType==='debit' && (
          <div style={{ marginBottom:12 }}>
            <p style={{ color:T.textMuted, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>Debit Type</p>
            <div style={{ display:'flex', gap:8 }}>
              {[
                ['recovery', 'Platform Recovery', 'Reclaiming platform funds'],
                ['transfer', 'User Transfer',     'Will credit another user'],
              ].map(([val,lbl,desc]) => (
                <div key={val} onClick={() => setDebitType(val)}
                  style={{ flex:1, padding:'10px 12px', borderRadius:7, border:`1px solid ${debitType===val?T.no:T.border}`, background:debitType===val?T.noDim:'transparent', cursor:'pointer', transition:'all 0.12s' }}>
                  <p style={{ color:debitType===val?T.no:T.text, fontSize:13, fontWeight:600, marginBottom:2 }}>{lbl}</p>
                  <p style={{ color:T.textMuted, fontSize:11 }}>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Transfer user selector */}
        {((adjustType==='credit' && creditType==='transfer') || (adjustType==='debit' && debitType==='transfer')) && (
          <div style={{ marginBottom:12 }}>
            <p style={{ color:T.textMuted, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>
              {adjustType==='credit' ? 'Debit From User' : 'Credit To User'}
            </p>
             <input
              value={transferUserSearch}
              onChange={e => { setTransferUserSearch(e.target.value); setTransferUserId(''); }}
              placeholder="Search name or phone..."
              style={input}
            />
            {transferUserSearch && !transferUserId && (
              <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:7, marginTop:4, maxHeight:160, overflowY:'auto' }}>
                {users
                  .filter(usr => String(usr.id) !== String(u.id) &&
                    (usr.full_name?.toLowerCase().includes(transferUserSearch.toLowerCase()) ||
                     usr.phone?.includes(transferUserSearch)))
                  .slice(0, 6)
                  .map(usr => (
                    <div key={usr.id}
                      onClick={() => { setTransferUserId(usr.id); setTransferUserSearch(`${usr.full_name} — KES ${parseFloat(usr.balance||0).toLocaleString()}`); }}
                      style={{ padding:'10px 12px', cursor:'pointer', borderBottom:`1px solid ${T.border}`, fontSize:13 }}
                      onMouseEnter={e => e.currentTarget.style.background=T.surfaceHi}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <span style={{ color:T.text, fontWeight:600 }}>{usr.full_name}</span>
                      <span style={{ color:T.textMuted, fontSize:12, marginLeft:8 }}>{usr.phone}</span>
                      <span style={{ color:T.yes, fontFamily:"'DM Mono',monospace", fontSize:12, float:'right' }}>KES {parseFloat(usr.balance||0).toLocaleString()}</span>
                    </div>
                  ))
                }
              </div>
            )}
          </div>
        )}
      </>
    )}

    {/* Amount and reason */}
    <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:10, marginBottom:10 }}>
      <input
        type="number"
        value={adjustAmount}
        onChange={e => { setAdjustAmount(e.target.value); if (selectedPending) setSelectedPending(null); }}
        placeholder="Amount"
        style={{ ...input, fontFamily:"'DM Mono',monospace" }}
      />
      <input value={adjustReason} onChange={e => setAdjustReason(e.target.value)} placeholder="Reason (logged in audit trail)" style={input}/>
    </div>

    {selectedPending && (
      <div style={{ background:T.goldDim, border:`1px solid ${T.gold}33`, borderRadius:7, padding:'8px 12px', marginBottom:10 }}>
        <p style={{ color:T.gold, fontSize:12, fontWeight:600 }}>✓ Linked to pending deposit — KES {parseFloat(selectedPending.amount).toLocaleString()} will be credited and transaction marked completed</p>
      </div>
    )}

    <div style={{ display:'flex', gap:8 }}>
      <button
        onClick={() => handleAdjustBalance(u.id)}
        disabled={!adjustAmount || !adjustReason || (!selectedPending && !adjustType)}
        style={{ background:(!adjustAmount||!adjustReason)?T.surfaceHi:adjustType==='debit'?T.no:T.yes, color:(!adjustAmount||!adjustReason)?T.textDim:'#fff', border:'none', borderRadius:7, padding:'8px 16px', fontWeight:700, cursor:(!adjustAmount||!adjustReason)?'not-allowed':'pointer', fontSize:13 }}>
        {selectedPending ? 'Credit & Clear Pending' : adjustType==='debit' ? 'Debit User' : 'Credit User'}
      </button>
      <button onClick={() => { setAdjusting(null); setSelectedPending(null); setPendingDeposits([]); setAdjustType(''); setCreditType(''); setDebitType(''); setTransferUserId(''); setTransferUserSearch(''); }}
        style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:7, padding:'8px 12px', cursor:'pointer', fontSize:13 }}>Cancel</button>
    </div>
  </div>
)}
                {messaging===u.id && (
  <div style={{ marginTop:12, background:T.bg, border:`1px solid ${T.accent}33`, borderRadius:8, padding:14 }}>
    <p style={{ color:T.accent, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:10 }}>
      Send message to {u.full_name}
    </p>
    <textarea
      value={messageText}
      onChange={e => setMessageText(e.target.value)}
      placeholder="Type your message to this user..."
      maxLength={500}
      rows={3}
      style={{ ...input, resize:'vertical', lineHeight:1.6, minHeight:80, marginBottom:10 }}
    />
    <p style={{ color:T.textDim, fontSize:11, marginBottom:10 }}>
      {messageText.length}/500 · User will see this in their notifications
    </p>
    <div style={{ display:'flex', gap:8 }}>
      <button
        onClick={() => handleSendMessage(u.id)}
        disabled={!messageText.trim()}
        style={{ background:!messageText.trim()?T.surfaceHi:T.accent, color:!messageText.trim()?T.textDim:'#fff', border:'none', borderRadius:7, padding:'8px 16px', fontWeight:700, cursor:!messageText.trim()?'not-allowed':'pointer', fontSize:13 }}>
        Send Message
      </button>
      <button onClick={() => { setMessaging(null); setMessageText(''); }} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:7, padding:'8px 12px', cursor:'pointer', fontSize:13 }}>Cancel</button>
    </div>
  </div>
)}
                {viewingTxns===u.id && (
                  <div style={{ marginTop:12, background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:14 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                      <p style={{ color:T.textMuted, fontSize:12, fontWeight:600 }}>Last 100 transactions</p>
                      <button onClick={() => setViewingTxns(null)} style={{ background:'transparent', color:T.textMuted, border:'none', cursor:'pointer', fontSize:13 }}>✕</button>
                    </div>
                    {userTxns.length===0
                      ?<p style={{ color:T.textDim, fontSize:13 }}>No transactions found</p>
                      :userTxns.map(t => (
                        <div key={t.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:`1px solid ${T.border}` }}>
                          <div>
                            <p style={{ fontSize:12, color:T.text, fontWeight:500 }}>
                              {({ bet:'Trade', platform_fee:'Entry Fee (2%)', sell:'Position Sold', deposit:'Deposit', withdrawal:'Withdrawal', payout:'Market Payout', refund:'Market Refund' }[t.type]||t.type?.replace(/_/g,' '))}
                            </p>
                            <p style={{ fontSize:11, color:T.textMuted }}>{new Date(t.created_at).toLocaleString()} {t.reference?`· ${t.reference}`:''}</p>
                          </div>
                          <span style={{ fontFamily:"'DM Mono',monospace", fontSize:13, fontWeight:700, color:['bet','platform_fee','withdrawal'].includes(t.type)?T.no:T.yes }}>
                            {['bet','platform_fee','withdrawal'].includes(t.type)?'-':'+'}KES {parseFloat(t.amount).toLocaleString()}
                          </span>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── FINANCIALS ── */}
        {tab==='financials' && user?.is_super_admin && (
          <div>
            {!financials
              ?<p style={{ color:T.textMuted, fontSize:13 }}>Loading...</p>
              : <>
                <button onClick={async () => {
                  try {
                    await axios.post(`${API}/api/mpesa/balance/query`, {}, { headers: { Authorization: `Bearer ${token}` } });
                    notify('Balance query sent — refreshing in 15 seconds');
                    setTimeout(fetchFinancials, 15000);
                  } catch (err) {
                    notify('Failed to query balance', true);
                  }
                }} style={{ background:T.accentDim, color:T.accent, border:`1px solid ${T.accent}22`, borderRadius:7, padding:'9px 18px', fontWeight:600, cursor:'pointer', fontSize:13, marginBottom:16 }}>
                  🔄 Query Till Balance
                </button>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
                  {[
                    { label:'Total User Balances', value:financials.total_user_balances, color:T.yes, prefix:'KES ' },
                    { label:'Total Deposits', value:financials.total_deposits, color:T.yes, prefix:'KES ' },
                    { label:'Total Withdrawals', value:financials.total_withdrawals, color:T.no, prefix:'KES ' },
                    { label:'Total Bets Volume', value:financials.total_bets_volume, color:T.accent, prefix:'KES ' },
                    { label:'Total Payouts', value:financials.total_payouts, color:T.no, prefix:'KES ' },
                    { label:'Pending Deposits', value:financials.pending_deposits, color:T.gold, prefix:'KES ' },
                    { label:'Total Users', value:financials.total_users, color:T.text, prefix:'' },
                    { label:'Verified Users', value:financials.verified_users, color:T.yes, prefix:'' },
                    { label:'Suspended Users', value:financials.suspended_users, color:T.no, prefix:'' },
                    { label:'Active Bets Count', value:financials.active_bets_count, color:T.accent, prefix:'' },
                    { label:'Active Bets Volume', value:financials.active_bets_volume, color:T.accent, prefix:'KES ' },
                    { label:'Till Balance (M-PESA)', value:financials.till_balance ?? '—', color:T.yes, prefix:financials.till_balance !== null ? 'KES ' : '' },
                    { label:'Float Health', value:financials.float_health !== null ? financials.float_health : '—', color:financials.float_health >= 0 ? T.yes : T.no, prefix:financials.float_health !== null ? 'KES ' : '' },
                  ].map(s => (
                    <div key={s.label} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:9, padding:'16px 18px' }}>
                      <p style={{ color:T.textMuted, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>{s.label}</p>
                      <p style={{ color:s.color, fontSize:20, fontWeight:800, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>
                        {s.prefix}{typeof s.value==='number'?s.value.toLocaleString(undefined,{maximumFractionDigits:0}):s.value}
                      </p>
                    </div>
                  ))}
                </div>

                <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:9, padding:'16px 18px', marginBottom:12 }}>
  <p style={{ color:T.textMuted, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>Platform Revenue</p>
  <p style={{ color: financials.platform_revenue >= 0 ? T.yes : T.no, fontSize:20, fontWeight:800, fontFamily:"'DM Mono',monospace", lineHeight:1, marginBottom:12 }}>
    KES {financials.platform_revenue.toLocaleString(undefined,{maximumFractionDigits:0})}
  </p>
  <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
    {[
      { label:'Bet Fees (2%)',       value:financials.fee_breakdown?.bet_fees||0,        color:T.accent },
      { label:'Sell Fees (1%)',       value:financials.fee_breakdown?.sell_fees||0,       color:T.gold },
      { label:'Withdrawal Fees (2%)', value:financials.fee_breakdown?.withdrawal_fees||0, color:T.yes },
      { label:'Resolution Fees (2%)', value:financials.fee_breakdown?.resolution_fees||0, color:T.purple },
      { label:'Creator Fees (70%)',   value:Math.abs(financials.fee_breakdown?.creator_fee||0),    color:T.gold   },
      { label:'Chama Upgrades',       value:financials.fee_breakdown?.chama_upgrade_fees||0,        color:T.purple },
       
    ].map(f => (
      <div key={f.label} style={{ flex:1, minWidth:100, background:T.bg, border:`1px solid ${T.border}`, borderRadius:7, padding:'10px 12px' }}>
        <p style={{ color:T.textMuted, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6 }}>{f.label}</p>
        <p style={{ color:f.color, fontSize:14, fontWeight:800, fontFamily:"'DM Mono',monospace" }}>KES {f.value.toLocaleString(undefined,{maximumFractionDigits:0})}</p>
        {f.label==='Creator Fees (70%)'&&<p style={{ color:T.textDim, fontSize:10, marginTop:4 }}>Paid to creators</p>}
        {f.label==='Admin Credits'&&<p style={{ color:T.textDim, fontSize:10, marginTop:4 }}>Paid out to users</p>}
        {f.label==='Admin Debits'&&<p style={{ color:T.textDim, fontSize:10, marginTop:4 }}>Reclaimed from users</p>}
      </div>
    ))}
  </div>
</div>

{/* Debit Reversible Balance */}
<div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:9, padding:'16px 18px', marginBottom:12 }}>
  <p style={{ color:T.textMuted, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>Reversible Debit Balance</p>
  {(() => {
    const remaining = reversibleDebitBalance;
    return (
      <>
        <p style={{ color: remaining > 0 ? T.gold : T.textMuted, fontSize:20, fontWeight:800, fontFamily:"'DM Mono',monospace", lineHeight:1, marginBottom:8 }}>
          KES {remaining.toLocaleString(undefined,{maximumFractionDigits:2})}
        </p>
        <p style={{ color:T.textDim, fontSize:12 }}>
          Debits: KES {((financials.fee_breakdown?.admin_debits_recovery||0) + (financials.fee_breakdown?.admin_debits_transfer||0) + (financials.fee_breakdown?.seed_debit_unlocked||0)).toLocaleString()} — Reversals: KES {((financials.fee_breakdown?.admin_credits_reversal||0) + (financials.fee_breakdown?.seed_debit_locked||0)).toLocaleString()}
        </p>
      </>
    );
  })()}
</div>

{/* WHT Section */}
<div style={{ background:T.surface, border:`1px solid ${T.no}44`, borderRadius:9, padding:'16px 18px', marginBottom:12 }}>
  <p style={{ color:T.no, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>⚠️ Withholding Tax (KRA)</p>
  <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16 }}>
    {[
      { label:'WHT Collected', value:financials.fee_breakdown?.wht_collected||0, color:T.no },
      { label:'WHT Remitted',  value:financials.fee_breakdown?.wht_remitted||0,  color:T.yes },
      { label:'WHT Outstanding', value:financials.fee_breakdown?.wht_outstanding||0, color:financials.fee_breakdown?.wht_outstanding > 0 ? T.gold : T.yes },
    ].map(f => (
      <div key={f.label} style={{ flex:1, minWidth:100, background:T.bg, border:`1px solid ${T.border}`, borderRadius:7, padding:'10px 12px' }}>
        <p style={{ color:T.textMuted, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6 }}>{f.label}</p>
        <p style={{ color:f.color, fontSize:14, fontWeight:800, fontFamily:"'DM Mono',monospace" }}>KES {f.value.toLocaleString(undefined,{maximumFractionDigits:2})}</p>
      </div>
    ))}
  </div>
  {/* KRA Remittance Form */}
  <div style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:'14px 16px' }}>
    <p style={{ color:T.textMuted, fontSize:12, fontWeight:600, marginBottom:10 }}>Remit to KRA</p>
    <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
      <input
  type="number"
  value={kraRemitAmount}
  onChange={e => setKraRemitAmount(e.target.value)}
  placeholder={`Amount (max KES ${(financials.fee_breakdown?.wht_outstanding||0).toFixed(2)})`}
  style={{ ...input, width:200, fontFamily:"'DM Mono',monospace" }}
/>
      <input
  type="text"
  value={kraRemitRef}
  onChange={e => setKraRemitRef(e.target.value)}
  placeholder="iTax payment reference"
  style={{ ...input, flex:1 }}
/>
      <button
        onClick={async () => {
          if (!kraRemitAmount || !kraRemitRef) return notify('Amount and iTax reference are required', true);
try {
  const r = await axios.post(
    `${API}/api/users/admin/remit-kra`,
    { amount: parseFloat(kraRemitAmount), reference: kraRemitRef },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  notify(r.data.message);
  setKraRemitAmount('');
  setKraRemitRef('');
  fetchFinancials();
          } catch (err) {
            notify(err.response?.data?.message || 'Failed to remit', true);
          }
        }}
        style={{ background:T.no, color:'#fff', border:'none', borderRadius:7, padding:'10px 20px', fontWeight:700, cursor:'pointer', fontSize:13, whiteSpace:'nowrap' }}
      >
        Remit to KRA
      </button>
    </div>
  </div>
</div>

                {/* KRA Report — ✅ FIXED: uses authenticated downloadCSV */}
                <div style={{ background:T.surface, border:`1px solid ${T.no}33`, borderRadius:9, padding:'16px 18px' }}>
                  <p style={{ color:T.textMuted, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>KRA Withholding Tax Report</p>
                  <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                    <select value={kraMonth} onChange={e => setKraMonth(parseInt(e.target.value))} style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:7, padding:'8px 12px', color:T.text, fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", outline:'none' }}>
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m,i) => (
                        <option key={i+1} value={i+1}>{m}</option>
                      ))}
                    </select>
                    <select value={kraYear} onChange={e => setKraYear(parseInt(e.target.value))} style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:7, padding:'8px 12px', color:T.text, fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", outline:'none' }}>
                      {[0,1,2].map(offset => { const yr=new Date().getFullYear()-offset; return <option key={yr} value={yr}>{yr}</option>; })}
                    </select>
                    <button
                      disabled={!!exportingCSV}
                      onClick={() => handleExportCSV(
                        `/api/users/admin/export/kra?year=${kraYear}&month=${kraMonth}`,
                        `mino-kra-withholding-${kraYear}-${String(kraMonth).padStart(2,'0')}.csv`
                      )}
                      style={{ background:T.noDim, color:T.no, border:`1px solid ${T.no}33`, borderRadius:7, padding:'8px 18px', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:"'DM Sans',sans-serif", whiteSpace:'nowrap', opacity:exportingCSV?0.6:1 }}>
                      {exportingCSV?'Exporting...':'Export KRA CSV'}
                    </button>
                    <span style={{ color:T.textMuted, fontSize:12, fontFamily:"'DM Mono',monospace" }}>
                      {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][kraMonth-1]} {kraYear}
                    </span>
                  </div>
                  <p style={{ color:T.textDim, fontSize:11, marginTop:10, lineHeight:1.6 }}>One row per winning bet · Includes user name, phone, tax withheld, and bet reference · Total at bottom = P10 iTax return amount</p>
                </div>
              </>
            }
          </div>
        )}

        {tab==='violations' && user?.is_super_admin && <ViolationsTab token={token} T={T}/>}

        {/* ── BROADCAST ── */}
        {tab==='broadcast' && user?.is_super_admin && (
          <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:28 }}>
            <h2 style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:6 }}>Broadcast Message</h2>
            <p style={{ color:T.textMuted, fontSize:13, marginBottom:24 }}>Send a notification to all active users instantly.</p>
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <label style={label}>Message Type</label>
                <div style={{ display:'flex', gap:8 }}>
                  {[['info','📢 Info'],['won','🎉 Announcement'],['refund','⚠️ Warning']].map(([val,lbl]) => (
                    <button key={val} type="button" onClick={() => setBroadcastType(val)} style={{ flex:1, padding:9, borderRadius:7, border:`1px solid ${broadcastType===val?T.accent:T.border}`, background:broadcastType===val?T.accentDim:'transparent', color:broadcastType===val?T.accent:T.textMuted, fontWeight:600, cursor:'pointer', fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div><label style={label}>Title (optional)</label><input value={broadcastTitle} onChange={e => setBroadcastTitle(e.target.value)} placeholder="e.g. New markets available!" style={input}/></div>
              <div><label style={label}>Message</label><textarea value={broadcastMsg} onChange={e => setBroadcastMsg(e.target.value)} placeholder="e.g. We've just added 5 new markets..." rows={4} style={{ ...input, resize:'vertical', lineHeight:1.6, minHeight:100 }}/></div>
              <div style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:'12px 16px' }}>
                <p style={{ fontSize:12, color:T.textMuted, marginBottom:4 }}>Preview</p>
                <p style={{ fontSize:13, color:T.text }}>{broadcastTitle?`${broadcastTitle}: `:''}{broadcastMsg||'—'}</p>
              </div>
              <button disabled={!broadcastMsg.trim()||broadcastSending} onClick={async () => {
                setBroadcastSending(true);
                try {
                  const r = await axios.post(`${API}/api/broadcast`, { title:broadcastTitle, message:broadcastMsg, type:broadcastType }, { headers: { Authorization: `Bearer ${token}` } });
                  notify(r.data.message); setBroadcastMsg(''); setBroadcastTitle('');
                } catch (err) { notify(err.response?.data?.message||'Failed to send', true); }
                finally { setBroadcastSending(false); }
              }} style={{ background:!broadcastMsg.trim()?T.surfaceHi:T.accent, color:!broadcastMsg.trim()?T.textDim:'#fff', border:'none', borderRadius:7, padding:'11px 24px', fontWeight:700, cursor:!broadcastMsg.trim()?'not-allowed':'pointer', fontSize:13 }}>
                {broadcastSending?'Sending...':'Send to All Users'}
              </button>
            </div>
          </div>
        )}

        {/* ── MARKET PERFORMANCE ── */}
        {tab==='performance' && (
  <div>
    <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, overflow:'hidden' }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 110px 110px 80px 130px 110px 80px 110px', gap:12, padding:'10px 18px', borderBottom:`1px solid ${T.border}`, background:T.surfaceHi }}>
        {['Market','Volume','24h Vol','Traders','Fees Earned','Profit','Status','Created By'].map(h => (
          <span key={h} style={{ fontSize:10, fontWeight:700, color:T.textDim, textTransform:'uppercase', letterSpacing:'0.08em' }}>{h}</span>
        ))}
      </div>
      {marketPerf.length===0
        ?<p style={{ color:T.textDim, fontSize:13, padding:'40px 18px', textAlign:'center' }}>No markets yet</p>
        :marketPerf.map((m,i) => {
          const entryFees      = parseFloat(m.entry_fees || 0);
const resFees        = parseFloat(m.resolution_fees || 0);
const withholdingTax = parseFloat(m.withholding_tax || 0);
const totalFees      = entryFees + resFees;
const platformProfit = entryFees;

const fmtKES = (val) => {
  const n = parseFloat(val || 0);
  return n > 0 && n < 1 ? n.toFixed(2) : n.toLocaleString(undefined, { maximumFractionDigits: 0 });
};
          return (
            <div key={m.id}
              style={{ display:'grid', gridTemplateColumns:'1fr 110px 110px 80px 130px 110px 80px 110px', gap:12, padding:'12px 18px', borderBottom:i<marketPerf.length-1?`1px solid ${T.border}`:'none', alignItems:'center', cursor:'pointer' }}
              onClick={() => navigate(`/markets/${m.id}`)}
              onMouseEnter={e => e.currentTarget.style.background=T.surfaceHi}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              <div style={{ display:'flex', gap:10, alignItems:'center', minWidth:0 }}>
                {m.image_url
                  ?<img src={m.image_url} alt="" style={{ width:28,height:28,borderRadius:5,objectFit:'cover',flexShrink:0 }} onError={e=>e.target.style.display='none'}/>
                  :<div style={{ width:28,height:28,borderRadius:5,background:T.surfaceHi,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center' }}><span style={{ fontSize:8,color:T.textDim,fontWeight:700 }}>{m.category?.slice(0,3).toUpperCase()}</span></div>
                }
                <p style={{ fontSize:12, fontWeight:600, color:T.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.title}</p>
              </div>
              <span style={{ fontSize:12, fontWeight:700, color:T.yes, fontFamily:"'DM Mono',monospace" }}>
                KES {parseFloat(m.total_volume||0).toLocaleString()}
              </span>
              <span style={{ fontSize:12, color:m.volume_24h>0?T.accent:T.textDim, fontFamily:"'DM Mono',monospace" }}>
                {m.volume_24h>0?`KES ${parseFloat(m.volume_24h).toLocaleString()}`:'—'}
              </span>
              <span style={{ fontSize:12, color:T.textMuted, fontFamily:"'DM Mono',monospace" }}>
                {m.trader_count}
              </span>
              <div>
                <span style={{ fontSize:12, fontWeight:700, color:T.purple, fontFamily:"'DM Mono',monospace", display:'block' }}>
                  KES {fmtKES(totalFees)}
                </span>
                <span style={{ fontSize:10, color:T.textDim }}>
                  {fmtKES(entryFees)} entry · {fmtKES(resFees)} res.
                </span>
              </div>
              <span style={{ fontSize:12, fontWeight:700, color:platformProfit>0?T.yes:platformProfit<0?T.no:T.textMuted, fontFamily:"'DM Mono',monospace" }}>
                KES {fmtKES(platformProfit)}
              </span>
              <span style={{ fontSize:10, padding:'2px 8px', borderRadius:4, fontWeight:700, background:m.status==='open'?`${T.yes}18`:`${T.textDim}18`, color:m.status==='open'?T.yes:T.textDim, fontFamily:"'DM Mono',monospace" }}>
                {m.status.toUpperCase()}
              </span>
              <span style={{ fontSize:11, color:T.textMuted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {m.created_by_name || '—'}
              </span>
            </div>
          );
        })
      }
    </div>
  </div>
)}
          
      {/* ── CHAMAS ── */}
         {tab === 'chamas' && (
            <div>
            {chamasLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>Loading chamas...</div>
            ) : chamas.length === 0 ? (
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 60, textAlign: 'center', color: T.textDim }}>No chamas created yet</div>
            ) : chamas.map(chama => (
              <div key={chama.id} style={{ background: T.surface, border: `1px solid ${expandedChama === chama.id ? T.accent : T.border}`, borderRadius: 10, padding: 18, marginBottom: 10, transition: 'border-color 0.15s' }}>
                {/* Chama header row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <p style={{ fontWeight: 700, fontSize: 14, color: T.text }}>{chama.name}</p>
                      <span style={{
                        fontSize: 9, padding: '2px 7px', borderRadius: 3, fontWeight: 700,
                        background: chama.status === 'active' ? T.yesDim : T.noDim,
                        color: chama.status === 'active' ? T.yes : T.no,
                        fontFamily: "'DM Mono',monospace",
                      }}>{chama.status.toUpperCase()}</span>
                      {chama.vote_before_trade && (
                        <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 3, fontWeight: 700, background: T.goldDim, color: T.gold, fontFamily: "'DM Mono',monospace" }}>VOTE-GATED</span>
                      )}
                    </div>
                    <p style={{ color: T.textMuted, fontSize: 12, marginBottom: 2 }}>
                      Admin: @{chama.admin_username} · {chama.admin_phone}
                    </p>
                    <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
                      {[
                        { label: 'Pool Balance', value: `KES ${parseFloat(chama.wallet_balance).toLocaleString()}`, color: T.yes },
                        { label: 'Members', value: chama.member_count, color: T.accent },
                        { label: 'Total Contributed', value: `KES ${parseFloat(chama.total_contributed).toLocaleString()}`, color: T.textMuted },
                        { label: 'Trade Volume', value: `KES ${parseFloat(chama.total_trade_volume || 0).toLocaleString()}`, color: T.purple },
                        { label: 'Trades', value: chama.total_trades || 0, color: T.textMuted },
                      ].map(s => (
                        <div key={s.label}>
                          <p style={{ color: T.textDim, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>{s.label}</p>
                          <p style={{ color: s.color, fontSize: 13, fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>{s.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button onClick={() => {
                      if (expandedChama === chama.id) { setExpandedChama(null); return; }
                      setExpandedChama(chama.id);
                      fetchChamaMembers(chama.id);
                      fetchChamaTxns(chama.id);
                      fetchChamaDisputes(chama.id);
                    }} style={{ background: T.accentDim, color: T.accent, border: `1px solid ${T.accent}22`, borderRadius: 6, padding: '6px 12px', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>
                      {expandedChama === chama.id ? 'Collapse' : 'View'}
                    </button>
                    <button onClick={() => setAdjustingChama(adjustingChama === chama.id ? null : chama.id)}
                      style={{ background: T.goldDim, color: T.gold, border: `1px solid ${T.gold}22`, borderRadius: 6, padding: '6px 12px', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>
                      Adjust
                    </button>
                    <button onClick={() => { setMessagingChama(messagingChama === chama.id ? null : chama.id); setChamaMessageText(''); }}
                      style={{ background: T.accentDim, color: T.accent, border: `1px solid ${T.accent}22`, borderRadius: 6, padding: '6px 12px', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>
                      Message Admin
                    </button>
                    {chama.is_frozen ? (
                      <button onClick={async () => {
                        try {
                          await axios.post(`${API}/api/chamas/admin/${chama.id}/unfreeze`, {}, { headers: { Authorization: `Bearer ${token}` } });
                          notify('Chama unfrozen'); fetchChamas();
                        } catch (err) { notify(err.response?.data?.error || 'Failed to unfreeze', true); }
                      }} style={{ background: T.yesDim, color: T.yes, border: `1px solid ${T.yes}22`, borderRadius: 6, padding: '6px 12px', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>
                        Unfreeze
                      </button>
                    ) : (
                      <button onClick={async () => {
                        const reason = prompt('Reason for freezing this chama?');
                        if (!reason) return;
                        try {
                          await axios.post(`${API}/api/chamas/admin/${chama.id}/freeze`, { reason }, { headers: { Authorization: `Bearer ${token}` } });
                          notify('Chama frozen'); fetchChamas();
                        } catch (err) { notify(err.response?.data?.error || 'Failed to freeze', true); }
                      }} style={{ background: T.noDim, color: T.no, border: `1px solid ${T.no}22`, borderRadius: 6, padding: '6px 12px', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>
                        Freeze
                      </button>
                    )}
                    
                  </div>
                </div>

                {/* Adjust panel */}
                {adjustingChama === chama.id && (
                  <div style={{ marginTop: 14, background: T.bg, border: `1px solid ${T.gold}33`, borderRadius: 8, padding: 14 }}>
                    <p style={{ color: T.gold, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>Adjust Pool Balance</p>
                    {/* Credit / Debit toggle */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                      {[['credit', 'Credit Pool'], ['debit', 'Debit Pool']].map(([val, lbl]) => (
                        <button key={val} type="button" onClick={() => { setChamaAdjType(val); setChamaAdjAmount(''); }}
                          style={{ flex: 1, padding: '8px 0', borderRadius: 7, fontWeight: 600, cursor: 'pointer', fontSize: 13, fontFamily: "'DM Sans',sans-serif",
                            border: `1px solid ${chamaAdjType === val ? (val === 'credit' ? T.yes : T.no) : T.border}`,
                            background: chamaAdjType === val ? (val === 'credit' ? T.yesDim : T.noDim) : 'transparent',
                            color: chamaAdjType === val ? (val === 'credit' ? T.yes : T.no) : T.textMuted,
                          }}>{lbl}</button>
                      ))}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginBottom: 10 }}>
                      <input type="number" value={chamaAdjAmount} onChange={e => setChamaAdjAmount(e.target.value)}
                        placeholder="Amount (KES)"
                        style={{ ...input, fontFamily: "'DM Mono',monospace" }} />
                      <input value={chamaAdjReason} onChange={e => setChamaAdjReason(e.target.value)}
                        placeholder="Reason (logged in audit trail)" style={input} />
                    </div>
                    {chamaAdjAmount && (
                      <div style={{ background: chamaAdjType === 'credit' ? T.yesDim : T.noDim, border: `1px solid ${chamaAdjType === 'credit' ? T.yes : T.no}33`, borderRadius: 7, padding: '8px 12px', marginBottom: 10 }}>
                        <p style={{ color: chamaAdjType === 'credit' ? T.yes : T.no, fontSize: 12, fontWeight: 600 }}>
                          {chamaAdjType === 'credit' ? '+ ' : '− '}KES {parseFloat(chamaAdjAmount || 0).toLocaleString()} will be {chamaAdjType === 'credit' ? 'added to' : 'deducted from'} the pool
                        </p>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => handleChamaAdjust(chama.id)}
                        disabled={!chamaAdjAmount || !chamaAdjReason}
                        style={{ background: !chamaAdjAmount || !chamaAdjReason ? T.surfaceHi : chamaAdjType === 'debit' ? T.no : T.yes, color: !chamaAdjAmount || !chamaAdjReason ? T.textDim : '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontWeight: 700, cursor: !chamaAdjAmount || !chamaAdjReason ? 'not-allowed' : 'pointer', fontSize: 13 }}>
                        {chamaAdjType === 'debit' ? 'Debit Pool' : 'Credit Pool'}
                      </button>
                      <button onClick={() => { setAdjustingChama(null); setChamaAdjAmount(''); setChamaAdjReason(''); setChamaAdjType('credit'); }}
                        style={{ background: 'transparent', color: T.textMuted, border: `1px solid ${T.border}`, borderRadius: 7, padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Message Admin panel */}
                {messagingChama === chama.id && (
                  <div style={{ marginTop: 14, background: T.bg, border: `1px solid ${T.accent}33`, borderRadius: 8, padding: 14 }}>
                    <p style={{ color: T.accent, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
                      Message @{chama.admin_username} (Chama Admin)
                    </p>
                    <textarea value={chamaMessageText} onChange={e => setChamaMessageText(e.target.value)}
                      placeholder="Message to the chama admin..." maxLength={500} rows={3}
                      style={{ ...input, resize: 'vertical', lineHeight: 1.6, minHeight: 80, marginBottom: 8 }} />
                    <p style={{ color: T.textDim, fontSize: 11, marginBottom: 10 }}>{chamaMessageText.length}/500 · Delivered to their notifications</p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => handleMessageChamaAdmin(chama)}
                        disabled={!chamaMessageText.trim()}
                        style={{ background: !chamaMessageText.trim() ? T.surfaceHi : T.accent, color: !chamaMessageText.trim() ? T.textDim : '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontWeight: 700, cursor: !chamaMessageText.trim() ? 'not-allowed' : 'pointer', fontSize: 13 }}>
                        Send Message
                      </button>
                      <button onClick={() => { setMessagingChama(null); setChamaMessageText(''); }}
                        style={{ background: 'transparent', color: T.textMuted, border: `1px solid ${T.border}`, borderRadius: 7, padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}


                {/* Expanded: members + transactions */}
                {expandedChama === chama.id && (
                  <div style={{ marginTop: 16 }}>
                    {/* Members */}
                    <p style={{ color: T.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Members</p>
                    <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
                      {(chamaMembers[chama.id] || []).map((m, i, arr) => (
                        <div key={m.user_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                          <div>
                            <span style={{ color: T.text, fontSize: 13, fontWeight: 500 }}>@{m.username}</span>
                            <span style={{ color: T.textDim, fontSize: 11, marginLeft: 8 }}>{m.phone}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                            <span style={{ fontSize: 11, color: T.textMuted, fontFamily: "'DM Mono',monospace" }}>
                              {parseFloat(m.share_pct).toFixed(1)}% share · KES {parseFloat(m.contributed).toLocaleString()} contributed
                            </span>
                            <span style={{
                              fontSize: 9, padding: '2px 7px', borderRadius: 3, fontWeight: 700,
                              background: m.role === 'admin' ? T.accentDim : m.role === 'trader' ? T.yesDim : m.role === 'treasurer' ? T.goldDim : T.surfaceHi,
    color: m.role === 'admin' ? T.accent : m.role === 'trader' ? T.yes : m.role === 'treasurer' ? T.gold : T.textMuted,
                              fontFamily: "'DM Mono',monospace", textTransform: 'uppercase',
                            }}>{m.role}</span>
                            <span style={{
                              fontSize: 9, padding: '2px 7px', borderRadius: 3, fontWeight: 700,
                              background: m.status === 'active' ? T.yesDim : T.noDim,
                              color: m.status === 'active' ? T.yes : T.no,
                              fontFamily: "'DM Mono',monospace",
                            }}>{m.status}</span>
                          </div>
                        </div>
                      ))}
                      {!(chamaMembers[chama.id]?.length) && (
                        <div style={{ padding: '20px', textAlign: 'center', color: T.textDim, fontSize: 13 }}>Loading members...</div>
                      )}
                    </div>

                    {/* Disputes */}
                    {(chamaDisputes[chama.id] || []).length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <p style={{ color: T.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Disputes</p>
                        <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden' }}>
                          {(chamaDisputes[chama.id] || []).map((d, i, arr) => (
                            <div key={d.id} style={{ padding: '12px 14px', borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                 <p style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 2 }}>{d.reason?.slice(0, 120) || '—'}</p>
                                  <p style={{ fontSize: 11, color: T.textMuted }}>
                                    Filed by @{d.filed_by_username} · {new Date(d.created_at).toLocaleDateString('en-KE')}
                                  </p>
                                  {d.resolution && (
                                    <p style={{ fontSize: 11, color: T.yes, marginTop: 4 }}>Resolution: {d.resolution}</p>
                                  )}
                                </div>
                                <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 3, fontWeight: 700, flexShrink: 0, fontFamily: "'DM Mono',monospace",
                                  background: d.status === 'resolved' ? T.yesDim : d.status === 'open' ? T.noDim : T.goldDim,
                                  color: d.status === 'resolved' ? T.yes : d.status === 'open' ? T.no : T.gold,
                                }}>{(d.status || 'open').toUpperCase()}</span>
                              </div>
                              {d.status !== 'resolved' && (
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  {[
    { key: 'upheld', label: 'Upheld', bg: T.yesDim, color: T.yes },
    { key: 'dismissed', label: 'Dismissed', bg: T.noDim, color: T.no },
    { key: 'warning', label: 'Warning', bg: T.goldDim, color: T.gold },
    ].map(res => (
    <button key={res.key} onClick={() => {
  const note = prompt(`Resolution note for "${res.label}" (required):`);
  if (!note?.trim()) return;
  handleResolveChamaDispute(chama.id, d.id, res.key, note.trim());
}}
    style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontWeight: 600,
      fontFamily: "'DM Sans',sans-serif", background: res.bg, color: res.color,
      border: `1px solid ${res.color}33`,
    }}>
    {res.label}
    </button>
    ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Transactions */}
                    <p style={{ color: T.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Recent Transactions</p>
                    <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
                      {(chamaTxns[chama.id] || []).slice(0, 20).map((tx, i, arr) => (
                        <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: i < Math.min(arr.length, 20) - 1 ? `1px solid ${T.border}` : 'none' }}>
                          <div>
                            <p style={{ fontSize: 12, color: T.text, fontWeight: 500, marginBottom: 2 }}>
                              {tx.type.replace(/_/g, ' ')}
                            </p>
                            <p style={{ fontSize: 11, color: T.textMuted }}>@{tx.username} · {new Date(tx.created_at).toLocaleString('en-KE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                          </div>
                          <span style={{
                            fontFamily: "'DM Mono',monospace", fontSize: 13, fontWeight: 700,
                            color: ['deposit', 'admin_credit', 'trade_returned'].includes(tx.type) ? T.yes : T.no,
                          }}>
                            {['deposit', 'admin_credit', 'trade_returned'].includes(tx.type) ? '+' : '-'}KES {parseFloat(tx.amount).toLocaleString()}
                          </span>
                        </div>
                      ))}
                      {!(chamaTxns[chama.id]?.length) && (
                        <div style={{ padding: '20px', textAlign: 'center', color: T.textDim, fontSize: 13 }}>Loading transactions...</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === 'disputes' && (
  <div>
    {disputesLoading ? (
      <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>Loading...</div>
    ) : allOpenDisputes.length === 0 ? (
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 60, textAlign: 'center', color: T.textDim }}>
        No open disputes — all chamas running smoothly
      </div>
    ) : allOpenDisputes.map(d => {
      const da = disputeActions[d.id] || {};
      const voteRatio = d.member_count > 0 ? (parseInt(d.votes_for_removal) / parseInt(d.member_count)) * 100 : 0;
      const isUnderReview = d.status === 'under_review';
      const borderColor = isUnderReview ? T.gold : T.no;
      const members_for_chama = chamaMembers[d.chama_id] || [];

      return (
        <div key={d.id} style={{ background: T.surface, border: `1px solid ${borderColor}44`, borderRadius: 10, padding: 18, marginBottom: 12 }}>

          {/* Header row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.accent }}>{d.chama_name}</span>
                <span style={{ color: T.textDim, fontSize: 11 }}>·</span>
                <span style={{ color: T.textMuted, fontSize: 11 }}>Filed by @{d.filed_by_username}</span>
                <span style={{ color: T.textDim, fontSize: 11 }}>· {new Date(d.created_at).toLocaleDateString('en-KE', { month: 'short', day: 'numeric' })}</span>
              </div>
              <p style={{ fontSize: 13, color: T.text, lineHeight: 1.6, marginBottom: 10 }}>{d.reason}</p>

              {/* Context strip */}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div>
                  <span style={{ color: T.textDim, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Pool</span>
                  <span style={{ color: T.yes, fontSize: 12, fontWeight: 700, fontFamily: "'DM Mono',monospace", marginLeft: 6 }}>
                    KES {parseFloat(d.chama_balance || 0).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span style={{ color: T.textDim, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Accused Admin</span>
                  <span style={{ color: T.accent, fontSize: 12, fontWeight: 600, marginLeft: 6 }}>@{d.chama_admin_username}</span>
                </div>
                {d.is_frozen && (
                  <span style={{ background: T.noDim, color: T.no, fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>🔒 FROZEN</span>
                )}
              </div>

              {/* Vote bar */}
              {parseInt(d.total_votes) > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: T.textDim, fontSize: 11 }}>
                      Member votes: {d.votes_for_removal} for removal / {d.total_votes} cast / {d.member_count} total
                    </span>
                    <span style={{ color: voteRatio >= 66 ? T.no : T.textDim, fontSize: 11, fontWeight: voteRatio >= 66 ? 700 : 400, fontFamily: "'DM Mono',monospace" }}>
                      {voteRatio.toFixed(0)}%
                    </span>
                  </div>
                  <div style={{ height: 4, background: T.border, borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(voteRatio, 100)}%`, background: voteRatio >= 66 ? T.no : T.gold, borderRadius: 2, transition: 'width 0.4s ease' }} />
                  </div>
                  {voteRatio >= 66 && (
                    <p style={{ color: T.no, fontSize: 11, fontWeight: 600, marginTop: 4 }}>⚠ Supermajority reached — requires action within 24h</p>
                  )}
                </div>
              )}
            </div>

            <span style={{ fontSize: 9, padding: '3px 9px', borderRadius: 3, fontWeight: 700, flexShrink: 0,
              background: isUnderReview ? T.goldDim : T.noDim,
              color: isUnderReview ? T.gold : T.no,
              fontFamily: "'DM Mono',monospace",
            }}>
              {isUnderReview ? 'UNDER REVIEW' : 'OPEN'}
            </span>
          </div>

          {/* Action selector */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: da.action ? 12 : 0 }}>
            {!isUnderReview && (
              <button
                onClick={() => handleDisputeUnderReview(d.chama_id, d.id)}
                style={{ fontSize: 12, padding: '6px 14px', borderRadius: 7, cursor: 'pointer', fontWeight: 600,
                  background: T.goldDim, color: T.gold, border: `1px solid ${T.gold}33`,
                  fontFamily: "'DM Sans',sans-serif",
                }}>
                🔍 Under Review
              </button>
            )}
            {['upheld', 'warning', 'dismissed'].map(action => {
              const styles = {
                upheld:   { bg: T.yesDim,    color: T.yes,      label: 'Upheld'    },
                warning:  { bg: T.goldDim,   color: T.gold,     label: 'Warning'   },
                dismissed:{ bg: T.noDim,     color: T.no,       label: 'Dismissed' },
              }[action];
              const isSelected = da.action === action;
              return (
                <button key={action}
                  onClick={() => {
                    updateDA(d.id, { action: isSelected ? null : action, note: da.note || '', transferEnabled: false, successorId: null, successorSearch: '' });
                    if (!isSelected && action === 'upheld') fetchChamaMembers(d.chama_id);
                  }}
                  style={{ fontSize: 12, padding: '6px 14px', borderRadius: 7, cursor: 'pointer', fontWeight: 600,
                    fontFamily: "'DM Sans',sans-serif",
                    background: isSelected ? styles.color : styles.bg,
                    color: isSelected ? '#fff' : styles.color,
                    border: `1px solid ${styles.color}44`,
                    transition: 'all 0.12s',
                  }}>
                  {styles.label}
                </button>
              );
            })}
          </div>

          {/* Resolution panel */}
          {da.action && (
            <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 14 }}>

              {/* Note input */}
              <p style={{ color: T.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                Resolution Note <span style={{ color: T.no }}>*</span>
                <span style={{ color: T.textDim, fontWeight: 400, textTransform: 'none', marginLeft: 6 }}>— shown to all parties</span>
              </p>
              <textarea
                value={da.note || ''}
                onChange={e => updateDA(d.id, { note: e.target.value })}
                placeholder={
                  da.action === 'upheld' ? 'Explain what the admin did wrong and what action is being taken...' :
                  da.action === 'warning' ? 'Describe what policy was violated and what will happen if repeated...' :
                  'Explain why this dispute does not meet the threshold for action...'
                }
                rows={3}
                maxLength={500}
                style={{ width: '100%', background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 7,
                  padding: '10px 12px', color: T.text, fontFamily: "'DM Sans',sans-serif", fontSize: 13,
                  outline: 'none', resize: 'vertical', lineHeight: 1.6, marginBottom: 8, boxSizing: 'border-box',
                }}
              />
              <p style={{ color: T.textDim, fontSize: 11, marginBottom: da.action === 'upheld' ? 12 : 0 }}>
                {(da.note || '').length}/500
              </p>

              {/* Successor transfer (upheld only) */}
              {da.action === 'upheld' && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: da.transferEnabled ? 10 : 0 }}>
                    <button
                      onClick={() => updateDA(d.id, { transferEnabled: !da.transferEnabled, successorId: null, successorSearch: '' })}
                      style={{ fontSize: 12, padding: '6px 14px', borderRadius: 7, cursor: 'pointer', fontWeight: 600,
                        fontFamily: "'DM Sans',sans-serif",
                        background: da.transferEnabled ? T.accentDim : T.surface,
                        color: da.transferEnabled ? T.accent : T.textMuted,
                        border: `1px solid ${da.transferEnabled ? T.accent + '44' : T.border}`,
                      }}>
                      {da.transferEnabled ? '✓ Transferring Admin Role' : '+ Transfer Admin Role'}
                    </button>
                    {!da.transferEnabled && (
                      <span style={{ color: T.textDim, fontSize: 11 }}>Optional — remove without reassigning</span>
                    )}
                  </div>

                  {da.transferEnabled && (
                    <div>
                      <p style={{ color: T.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Select new chama admin:</p>
                      {members_for_chama.length === 0 ? (
                        <p style={{ color: T.textDim, fontSize: 12 }}>Loading members...</p>
                      ) : (
                        <div style={{ background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 7, overflow: 'hidden' }}>
                          {members_for_chama
                            .filter(m => m.user_id !== d.chama_admin_id && m.status === 'active')
                            .map((m, i, arr) => (
                              <div key={m.user_id}
                                onClick={() => updateDA(d.id, { successorId: m.user_id })}
                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  padding: '9px 12px', cursor: 'pointer',
                                  borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : 'none',
                                  background: da.successorId === m.user_id ? T.accentDim : 'transparent',
                                  transition: 'background 0.1s',
                                }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {da.successorId === m.user_id && <span style={{ color: T.accent, fontSize: 12 }}>✓</span>}
                                  <span style={{ color: T.text, fontSize: 13, fontWeight: 500 }}>@{m.username}</span>
                                </div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                  <span style={{ color: T.textMuted, fontSize: 11, fontFamily: "'DM Mono',monospace" }}>
                                    {parseFloat(m.share_pct || 0).toFixed(1)}% share
                                  </span>
                                  <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 3, fontWeight: 700,
                                    background: { trader: T.yesDim, treasurer: T.goldDim, member: T.surfaceHi }[m.role] || T.surfaceHi,
                                    color: { trader: T.yes, treasurer: T.gold, member: T.textMuted }[m.role] || T.textMuted,
                                    fontFamily: "'DM Mono',monospace", textTransform: 'uppercase',
                                  }}>{m.role}</span>
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Confirm row */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  onClick={() => handleResolveChamaDispute(d.chama_id, d.id, da.action, da.note, da.successorId)}
                  disabled={!da.note?.trim() || (da.transferEnabled && !da.successorId)}
                  style={{ padding: '8px 20px', borderRadius: 7, border: 'none', fontWeight: 700, fontSize: 13,
                    fontFamily: "'DM Sans',sans-serif", cursor: (!da.note?.trim() || (da.transferEnabled && !da.successorId)) ? 'not-allowed' : 'pointer',
                    background: (!da.note?.trim() || (da.transferEnabled && !da.successorId)) ? T.surfaceHi :
                      da.action === 'upheld' ? T.yes : da.action === 'warning' ? T.gold : T.no,
                    color: (!da.note?.trim() || (da.transferEnabled && !da.successorId)) ? T.textDim : '#fff',
                  }}>
                  Confirm {da.action === 'upheld' ? 'Uphold' : da.action === 'warning' ? 'Warning' : 'Dismiss'}
                </button>
                <button
                  onClick={() => setDisputeActions(prev => { const n = { ...prev }; delete n[d.id]; return n; })}
                  style={{ padding: '8px 14px', borderRadius: 7, background: 'transparent', color: T.textMuted,
                    border: `1px solid ${T.border}`, cursor: 'pointer', fontSize: 13,
                  }}>
                  Cancel
                </button>
                {da.action === 'upheld' && da.transferEnabled && !da.successorId && (
                  <span style={{ color: T.textDim, fontSize: 11 }}>Select a successor to confirm</span>
                )}
              </div>
            </div>
          )}
        </div>
      );
    })}
  </div>
)}
 </div> 
    </div>    
  );
}